/**
 * Stress-majorization layout for a single space.
 *
 * One solver run handles a flat list of nodes + the directed edges between
 * them + any external ports they carry. The output is deterministic, warm-
 * starts from a previous run when given one, and respects pinned rectangles
 * (hand-placed items) as hard anchors.
 *
 * Energy terms (all accumulated into a per-node gradient each iteration):
 *   1. Connection stress — spring between endpoints of every edge. Each
 *      edge's ideal length depends on the *minimum* degree of its two
 *      endpoints (leaves tuck close to their hub; two hubs need room to
 *      fan out their other connections), with a bonus shortening if both
 *      endpoints share a team. Stiffness is damped by √(degᵢ·degⱼ) so
 *      hubs don't drag their neighbourhood into a tight ring.
 *   2. Flow bias — constant horizontal force per directed edge: source
 *      gets a leftward push, target gets a rightward push. On cycles the
 *      contributions cancel and geometry is free; on chains they accumulate
 *      so sources drift left, sinks drift right. No explicit layer
 *      assignment, no hinge, no rank computation.

 *   3. Axis alignment — per connected edge, a small force reduces the
 *      smaller-magnitude component of the chord (dx or dy). Rotates edges
 *      toward the nearest horizontal or vertical, producing a grid-ish feel
 *      without hard-constraining directions.
 *   4. All-pairs Coulomb repulsion — every pair of nodes repels with force
 *      ∝ 1/dist². Short-range dominant (spreads crowded clusters into
 *      breathable ones), fades at distance. The main reason the layout
 *      doesn't settle into a dense blob under connection stress.
 *   5. Parent gravity — weak pull toward the space's centroid. Keeps
 *      disconnected components from drifting to infinity.
 *   6. Port bias — each external port adds the same constant horizontal
 *      force as a directed edge: "out" pushes the owning module right, "in"
 *      pushes left. Per-port value is stronger than flow per edge because
 *      it encodes a space boundary.
 *
 * There is no hard non-overlap projection: the strong short-range
 * repulsion tier (force ∝ 1/d_rect² with a 1px floor, smoothed to zero
 * at the cutoff) keeps rectangles from collapsing into each other as a
 * smooth, differentiable force.
 *
 * All constants live at the top of this file and are named for what they
 * control so tuning is a single edit.
 */

export interface StressNode {
  readonly id: string;
  readonly w: number;
  readonly h: number;
  /** Optional grouping signals — contribute to community attraction. */
  readonly team?: string;
  readonly domain?: string;
}

export interface StressEdge {
  readonly from: string;
  readonly to: string;
}

export interface StressPort {
  readonly nodeId: string;
  readonly direction: PortDirection;
  /**
   * Stable id of the cross-space peer this port connects to. Two nodes in the
   * same space that carry a port to the SAME peer are pulled into one
   * component (see `findComponents`) so the solver clusters them — otherwise
   * they share no edge, become separate singleton CCs, and shelf-pack into a
   * flat row even though they all flow to the same external target.
   */
  readonly peerId?: string;
}

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface StressInput {
  readonly nodes: readonly StressNode[];
  readonly edges: readonly StressEdge[];
  readonly externalPorts?: readonly StressPort[];
  /** Positions from a previous solve; nodes present here warm-start. */
  readonly previous?: ReadonlyMap<string, Rect>;
  /** Hand-placed nodes; pinned as hard anchors, gradient frozen. */
  readonly pinned?: ReadonlyMap<string, Rect>;
  /**
   * Iteration budget hint:
   *   - "full"        — cold or significant-change solve (largest budget).
   *   - "incremental" — warm-start + small budget for local relaxation.
   *   - "drag"        — tiny per-call budget with damped forces. Called once
   *                     per drag frame while the user moves a node: each
   *                     frame nudges neighbours a little, so across dozens
   *                     of frames they drift gracefully into place instead
   *                     of snapping around. Think clouds settling, not
   *                     billiard balls.
   */
  readonly budget?: "full" | "incremental" | "drag";
  /**
   * P2 flag (board-layout visual contract): jurisdiction-scoped solve.
   * Nodes partition into NEIGHBORHOODS (partitionNeighborhoods); flow
   * inequalities apply only BETWEEN neighborhoods (and only outside the
   * level-2 hood-graph SCCs), and organize columns never cross a
   * neighborhood boundary. Off = the classic global-jurisdiction solve.
   */
  readonly neighborhoods?: boolean;
  /**
   * Forced neighborhood partition (RAW node id → group id) — the frame-
   * groups channel (`group by` views). Present ⇒ the hood-scoped path runs
   * regardless of the `neighborhoods` flag: mapped nodes are assigned to
   * `~group~<groupId>` hoods verbatim (partitionNeighborhoods, its memo,
   * and R6 hysteresis are all skipped for them — the assignment is
   * explicit and stable across edits by construction); unmapped nodes get
   * the normal topology partition over the remainder.
   */
  readonly partition?: ReadonlyMap<string, string>;
  /**
   * Nested `group by`: group id → parent group id (frame-in-frame). Only
   * meaningful alongside `partition` (which maps each node to its FINEST
   * group — a leaf, or a non-leaf for that level's ungrouped pool). Absent
   * ⇒ all groups are top-level (the single-level shape).
   */
  readonly partitionParents?: ReadonlyMap<string, string>;
  /**
   * Layout mode. "float" = standard gradient descent (default). "sand" =
   * each non-pinned node only moves when the summed gradient magnitude
   * exceeds SAND_F_STATIC, with SAND_F_KINETIC subtracted along the motion
   * direction once moving. Cold solves (budget === "full") bypass friction
   * so initial layout always converges cleanly; sand only applies to
   * "incremental" and "drag" budgets.
   */
  readonly mode?: "float" | "sand";
  /**
   * Gesture baseline for trim-anchor calibration (screen space). During a
   * drag the app passes the layout AS OF DRAG START here: standing forces
   * are measured against — and absorbed at — this state, so everything
   * the gesture itself adds acts as a genuine force delta instead of
   * being re-absorbed frame by frame. Omitted → the warm start is the
   * baseline (idle re-solves have no gesture deltas to preserve).
   *
   * For MODEL-EDIT gestures (see `editBaseline`) this is the pre-edit
   * layout and may include rects for nodes the edit REMOVED — they act as
   * phantom force sources during calibration so their disappearance is a
   * genuine release delta.
   */
  readonly trimBase?: ReadonlyMap<string, Rect>;
  /**
   * Model-edit gesture: the PRE-EDIT topology this solve's `trimBase` was
   * an equilibrium of. When present (incremental budget only), trim
   * anchors are calibrated against the baseline WORLD — old positions,
   * old sizes, old edges, old ports — instead of the current graph, so
   * only the edit's own force deltas act: a new edge reels its endpoints,
   * a spawned node nestles in, a removed node's neighbours relax, a
   * resize pushes exactly as far as the growth demands, and everything
   * the edit didn't touch holds. Same treatment as a drag, generalized:
   * a model edit IS a gesture.
   */
  readonly editBaseline?: {
    /** Pre-edit edge set (same id domain as `edges`). */
    readonly edges: readonly StressEdge[];
    /** Pre-edit external ports. Omitted → current ports. */
    readonly ports?: readonly StressPort[];
    /** Pre-edit forced partition (see `StressInput.partition`). The
     *  calibration world must classify edges — and apply the frame
     *  cohesion terms — under the PRE-EDIT assignment, or a membership move
     *  reads as phantom forces on untouched nodes. */
    readonly partition?: ReadonlyMap<string, string>;
    /** Pre-edit nesting (see `StressInput.partitionParents`). */
    readonly partitionParents?: ReadonlyMap<string, string>;
  };
  /**
   * Screen direction of the dependency flow (default "LR"). The solver
   * itself always works in flow space (flow = +x); the direction is applied
   * as an exact axis swap/mirror at the input and output boundaries — see
   * ./direction.ts. Outputs (including every yielded step) are in screen
   * space, as are `previous` and `pinned` inputs.
   */
  readonly direction?: FlowDirection;
}

export type LayoutMode = "float" | "sand";

/** One discrete unit of work in the solver, yielded by stressLayoutSteps.
 *  `ghost` = a gradient iter during the ghost-repair travel phase (mover is
 *  transparent to repulsion). `ghostSettle` = repulsion re-enabled, mover
 *  bouncing into a free spot. Both carry `moverId` so callers can highlight
 *  the travelling node. `align` = final tidy phase — sand-mode pass that
 *  snaps node top-left to a grid and boosts axis alignment. */
export type StressStepKind =
  | "iter"
  | "sgd"
  | "placeAtIdeal"
  | "ghost"
  | "ghostSettle"
  | "align"
  | "organize"
  | "separate";
export interface StressStep {
  readonly kind: StressStepKind;
  readonly result: StressResult;
  /** Round index (0-based); mostly for debugging. */
  readonly round: number;
  /** Ghost-repair mover id — set only on `ghost` / `ghostSettle` steps. */
  readonly moverId?: string;
}

export interface StressResult {
  readonly positions: Map<string, Rect>;
  readonly width: number;
  readonly height: number;
  /** Neighborhood mode only: RAW node id → hood id, exactly the
   *  partition this solve used. Feed back as the next edit solve's
   *  hysteresis reference, debug overlays, tests. */
  readonly neighborhoods?: ReadonlyMap<string, string>;
  /** Neighborhood mode only: RAW node id → connected-component index, the
   *  exact decomposition this solve shelf-packed (union-find over clean
   *  edges PLUS external-port peer unions — not re-derivable from edges
   *  alone). Data path for one-frame-per-(group × CC). */
  readonly components?: ReadonlyMap<string, number>;
}

// ---- Tuning constants ------------------------------------------------------
// Shared with diagnostics.ts via ./layoutParams so tuning one knob updates
// both the solver and the debug view in lockstep. Solver-only iteration
// params stay below.

import {
  NODE_W,
  NODE_H,
  IDEAL_BY_MIN_DEGREE,
  IDEAL_CAP,
  IDEAL_TEAM_BONUS,
  W_CONN,
  W_FLOW,
  W_ALIGN,
  W_PARENT,
  W_PORT,
  W_REPULSE_STRONG,
  REPULSE_STRONG_CUTOFF,
  REPULSE_STRONG_FLOOR,
  W_REPULSE_WEAK,
  SIBLING_REPULSE_SCALE,
  REPULSE_WEAK_CUTOFF,
  REPULSE_WEAK_FLOOR,
  SAND_F_STATIC,
  SAND_F_STATIC_HOLD,
  SAND_F_KINETIC,
  SEP_GAP,
  SEP_PER_CROWD,
  SEP_CROWD_MAX,
  SEP_MAX_PASSES,
  FRAME_PAD,
  FRAME_LABEL_H,
  W_FRAME_COHESION,
  FRAME_ANCESTOR_COHESION_DECAY,
  FRAME_SEP_MAX_PASSES,
  FLOW_SEP_FRAC,
  CROWD_BASE,
  CROWD_DEG_BASE,
  DENSITY_IDEAL_PER_CROWD,
  DENSITY_IDEAL_MAX,
} from "./layoutParams.js";
import {
  buildTargetDistances,
  intraSccEdgeKeys,
  sccComponents,
  meanRadius,
} from "./graphDistances.js";
import { sgdPhase, hashIds, type FlowTerm } from "./sgdStress.js";
import {
  separationPasses,
  resolveResidualOverlaps,
  frameSeparationPasses,
  resolveFrameStrangerResiduals,
} from "./separation.js";
import { organizeDecide } from "./organize.js";
import { partitionNeighborhoods } from "./neighborhoods.js";
import {
  detectMotifs,
  detectShelves,
  nearTwinReserved,
  buildAssemblies,
  SHELF_MIN_MEMBERS,
  type MotifAssembly,
} from "./motifs.js";
import { toFlowRect, fromFlowRect, isVertical, type FlowDirection } from "./direction.js";
import { PORT_H, PORT_GAP } from "./portGeom.js";
import type { PortDirection } from "./types.js";

const PADDING = 60;             // outer frame padding, matches old layout

/** Vertical extent a node's stacked wall pills occupy on one wall — matches the
 *  centred stack in app-shell `resolvePortPlacements` (n pills, PORT_GAP between).
 *  Used to size the band a ported hub's motif wing must clear. */
function portBandHeight(count: number): number {
  return count > 0 ? PORT_H * count + PORT_GAP * (count - 1) : 0;
}

/** Build a per-hub in/out band-height lookup from a port list, for
 *  `buildAssemblies` to split ported hubs' wings clear of their wall lanes. */
function bandOfPorts(
  ports: readonly StressPort[],
): (hubId: string) => { inBand: number; outBand: number } {
  const inN = new Map<string, number>();
  const outN = new Map<string, number>();
  for (const p of ports) {
    const m = p.direction === "out" ? outN : inN;
    m.set(p.nodeId, (m.get(p.nodeId) ?? 0) + 1);
  }
  return (hubId) => ({
    inBand: portBandHeight(inN.get(hubId) ?? 0),
    outBand: portBandHeight(outN.get(hubId) ?? 0),
  });
}

/** Deterministic 1-D avoidance: return `cy` unmoved if it clears every forbidden
 *  y-interval, else the NEARER edge of the one (disjoint after merge) interval it
 *  sits inside (tie → the lower edge). */
function nearestFreeY(cy: number, intervals: readonly (readonly [number, number])[]): number {
  if (intervals.length === 0) return cy;
  const sorted = [...intervals].sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const iv of sorted) {
    const last = merged[merged.length - 1];
    if (last && iv[0] <= last[1]) last[1] = Math.max(last[1], iv[1]);
    else merged.push([iv[0], iv[1]]);
  }
  for (const [lo, hi] of merged) {
    if (cy > lo && cy < hi) return cy - lo <= hi - cy ? lo : hi;
  }
  return cy;
}

const CORRIDOR_EPS = 0.5;
/** Clearance past a ported node's lane before a neighbour is considered clear —
 *  ≥ the P0 metric's CORRIDOR_BAND_MARGIN(24) plus slack, so a staggered node
 *  provably scores 0 intrusions rather than sitting on the boundary. */
const CORRIDOR_MARGIN = 28;

/**
 * P3 — after organize, stagger flow-chained ported nodes in Y so none sits in an
 * UPSTREAM ported node's wall lane (the "line over the node" for NON-motif
 * chains; motif hubs are already handled by the port-aware template). Works in
 * FLOW space: an out-port's lane runs toward +x, an in-port's toward −x (the same
 * orientation the wall-anchor phase uses). Only nodes VISIBLE in the coarse graph
 * (`pos`) are touched — a motif hub lives inside its `~motif~` super-node, so this
 * scopes naturally to the non-motif set. Pure Y projection: flow constrains x-order
 * only, so a Y shift never fights flow. Greedy nearest-free lane assignment is
 * IDEMPOTENT (a node already clear of every upstream band is left where it is), so
 * a re-solve reproduces the same staircase — the cold decision that the warm/
 * maintenance path then holds (this pass, like organize, runs cold-only).
 *
 * Band height mirrors the P0 metric: `max(node.h, pillStack)` — the connectors
 * emanate across the node face near it, then fan to the pill stack at the wall.
 */
function clearPortCorridors(
  nodeIds: readonly string[],
  pos: Map<string, { x: number; y: number }>,
  size: ReadonlyMap<string, { w: number; h: number }>,
  ports: readonly StressPort[],
  pinned: ReadonlyMap<string, unknown>,
): void {
  const outCount = new Map<string, number>();
  const inCount = new Map<string, number>();
  for (const p of ports) {
    if (!pos.has(p.nodeId) || pinned.has(p.nodeId)) continue;
    // `inout` counts toward OUT because that is the wall it docks on — the lane
    // this reserves has to be the lane the pill actually lands in
    // (`resolvePortPlacements` sends an unpinned inout port downstream).
    const m = p.direction === "in" ? inCount : outCount;
    m.set(p.nodeId, (m.get(p.nodeId) ?? 0) + 1);
  }
  // An owner's lane half-height, matching the P0 metric exactly (max of the node
  // face and the pill stack, plus a clearance margin ≥ the metric's so a cleared
  // node provably scores 0, not merely shrinks). A DOWNSTREAM node intrudes when
  // its centre is within `ownerHalf(owner) + intruder.h/2` of the owner's centre.
  const PORT_PITCH = PORT_H + PORT_GAP; // 36 — the metric's CORRIDOR_PILL_PITCH
  const ownerHalf = (id: string, count: number): number =>
    Math.max(size.get(id)!.h, count * PORT_PITCH) / 2 + CORRIDOR_MARGIN;
  // side: +1 = out (lane runs toward +x, "forward" = larger x); −1 = in (−x).
  const stagger = (counts: Map<string, number>, side: 1 | -1): void => {
    if (counts.size < 2) return; // a lone ported node has no upstream peer
    const ids = [...counts.keys()].sort((a, b) => {
      const pa = pos.get(a)!;
      const pb = pos.get(b)!;
      const ka = side === 1 ? pa.x : -pa.x; // upstream (nearer the source) first
      const kb = side === 1 ? pb.x : -pb.x;
      return ka - kb || (a < b ? -1 : 1);
    });
    const placed: { cx: number; cy: number; half: number }[] = [];
    for (const id of ids) {
      const p = pos.get(id)!;
      const s = size.get(id)!;
      const cx = p.x + s.w / 2;
      const halfMe = s.h / 2;
      const forbidden: [number, number][] = [];
      for (const u of placed) {
        const forward = side === 1 ? cx > u.cx : cx < u.cx;
        if (!forward) continue; // only clear lanes this node is DOWNSTREAM in
        forbidden.push([
          u.cy - u.half - halfMe - CORRIDOR_EPS,
          u.cy + u.half + halfMe + CORRIDOR_EPS,
        ]);
      }
      const cy = nearestFreeY(p.y + s.h / 2, forbidden);
      pos.set(id, { x: p.x, y: cy - s.h / 2 });
      placed.push({ cx, cy, half: ownerHalf(id, counts.get(id)!) });
    }
  };
  stagger(outCount, 1);
  stagger(inCount, -1);

  // Evict NON-ported intruders from a ported owner's wall lane. `stagger` only
  // moves ported nodes relative to each other, so a plain flow node with no
  // boundary ports (e.g. a router upstream of a bidirectional hub that got
  // pulled to the far wall by its majority port side) is left sitting in the
  // hub's in/out corridor — its connectors then cross straight over that node.
  // This runs even for a LONE ported owner (the case `stagger` skips). Only pure
  // flow nodes move (never a ported node), so the synthetic ported fixtures are
  // untouched; Y-only, so it never fights flow-x.
  const ported = new Set<string>([...outCount.keys(), ...inCount.keys()]);
  const evict = (counts: Map<string, number>, side: 1 | -1): void => {
    for (const [ownerId, c] of counts) {
      const o = pos.get(ownerId)!;
      const os = size.get(ownerId)!;
      const ocx = o.x + os.w / 2;
      const ocy = o.y + os.h / 2;
      const half = ownerHalf(ownerId, c);
      for (const nid of nodeIds) {
        if (nid === ownerId || ported.has(nid) || pinned.has(nid) || !pos.has(nid)) continue;
        const n = pos.get(nid)!;
        const ns = size.get(nid)!;
        const ncx = n.x + ns.w / 2;
        if (side === 1 ? ncx <= ocx : ncx >= ocx) continue; // only the port side
        const ncy = n.y + ns.h / 2;
        if (half + ns.h / 2 - Math.abs(ncy - ocy) <= 0) continue; // clear of the band
        // Push out to the nearer band edge (up if the node is at/above centre).
        pos.set(nid, { x: n.x, y: ncy <= ocy ? ocy - half - ns.h - CORRIDOR_EPS : ocy + half + CORRIDOR_EPS });
      }
    }
  };
  evict(outCount, 1);
  evict(inCount, -1);
}

// Component packing — independent weakly-connected components (CCs)
// solve in their own coord frames in parallel, then get shelf-packed onto
// the canvas so they don't overlap. Singleton (degree-zero) nodes are a
// special case: components of size 1. Default row width sized so the
// strip fits roughly 80% of a 1080p screen at 0.8 zoom (≈1536 screen px
// → 1920 space units).
const CC_PACK_GAP = 120;
const CC_PACK_ROW_WIDTH = 1920;

// Co-flow spring (findComponents): two nodes that share an external-port peer
// are unioned into one component to CLUSTER them, but they carry no edge
// between them — so buildTargetDistances falls back to its large "substitute"
// distance and pushes them far apart, defeating the union. A weak spring per
// direct peer-sharing pair gives the pair a real, clustered target instead.
// Generous ideal (loosest real-edge tier) + reduced stiffness so genuine
// edges still dictate the global shape; these only pull co-flow siblings out
// of the substitute-distance exile into neighbourly range.
const PEER_UNION_IDEAL = 200;
const PEER_UNION_STIFFNESS_SCALE = 0.5;

// Full/incremental budgets are split across multiple converge-repair rounds.
// Each round does (ITER_PER_ROUND) gradient-descent iterations followed by
// a structural repair pass. Re-running the gradient descent after each
// repair lets teleported nodes settle, and the next repair round often
// finds *different* long edges that the first repair freed up.
// Full solves get their global shape from the SGD stress phase; the spring
// rounds after it are local polish (silhouette gaps, axis alignment) plus
// the repair escape hatches. Two rounds suffice — more would slowly erode
// the stress optimum toward the springs' local-only view.
const ROUNDS_FULL = 2;
const ROUNDS_INCREMENTAL = 2;
// Doubled repeatedly to give each post-repair round enough relaxation
// time for the graph to settle before the next repair pass fires. The
// sequence placeAtIdeal → teleports introduces sudden disturbances;
// with too-short relaxation, the next repair saw transient stretches
// rather than true equilibrium and moved nodes unnecessarily.
const ITER_PER_ROUND_FULL = 400;
const ITER_PER_ROUND_INCREMENTAL = 160;
// Drag mode runs once per pointer-move event — which at 60Hz means 60×/sec.
// Total per-second neighbour drift budget ≈ ITER_DRAG × STEP_CAP_DRAG × fps.
// 2 iters × 1.2 units × 60fps = 144 units/sec — visible cloud drift without
// nodes streaking away under any gradient term.
const ITER_DRAG = 2;
const LR_START = 0.1;
const LR_END = 0.02;
const LR_DRAG = 0.04;
const STEP_CAP_START = 120;     // max per-iter move (space units)
const STEP_CAP_END = 3;
const STEP_CAP_DRAG = 1.2;
const CONVERGE_EPS = 0.15;      // avg squared move to declare convergence
// Margin baked into the seed lattice and the placeAtIdeal "free spot"
// search, keeping initial and candidate placements well-separated. Not
// enforced as a hard constraint during solving — strong short-range
// repulsion handles any collisions that arise as a smooth force.
const OVERLAP_GAP = 60;

// Tidy phase — runs once at the end of every non-drag solve. Friction
// (sand mode) lets aligned pairs lock, then the rest of the iters give
// the graph a long "settle in sand" tail so weakly-perturbed nodes drift
// onto grid lines and lock there. Sized for ~4s of animation playback at
// 60fps (240 frames); sync drain runs the same loop but completes in ms
// since sand keeps most nodes immobile.
const TIDY_ITERS = 240;
const TIDY_LR = 0.12;
const TIDY_STEP_CAP = 6;

// Formation settle — after organize DECIDES, the system converges under
// base forces + formation anchors. Convergence must be genuine (that's
// what makes the final state drag-stable), hence the generous cap and the
// tight epsilon (avg squared move per node).
const SETTLE_MAX_ITERS = 800;
const SETTLE_LR = 0.1;
const SETTLE_STEP_CAP = 8;
const SETTLE_CONVERGED_EPS = 0.02;
/** Formation-anchor spring stiffness: settle anneals from GENTLE (the
 *  base forces still shape things) up to HOLD (the state converges onto
 *  the decided formation within ~a pixel, making the next solve's
 *  re-derived decision trivially identical). Warm solves anchor at HOLD —
 *  the stiffness the final equilibrium was formed with. Implicit
 *  integration → no stability ceiling. */
const W_FORMATION = 4;
const W_FORMATION_HOLD = 1000;
/** Trim-anchor stiffness for warm solves: the standing layout is an exact
 *  equilibrium at ANY K (anchors are gradient-calibrated); K only sets how
 *  far things lean per unit of NEW force — the drag feel. */
const W_TRIM = 15;

// Wall-anchor settle — final phase, runs only when a space has external ports.
// Each ported node springs horizontally toward a wall column just outside the
// interior bbox (out → right, in → left). This is what makes an external
// connection actually shape the layout: the earlier per-edge "port bias" is a
// constant force that does nothing for a node whose only link is external (it
// lands in its own singleton component, skips the solver, and shelf-packs as
// if independent). The spring has a finite rest position so even a lone ported
// node is pulled to the wall and held there — stable, unlike a constant force.
const WALL_MARGIN = 120;       // gap from interior bbox to the wall column
const WALL_ANCHOR_ITERS = 300;
const WALL_ANCHOR_LR = 0.12;
const WALL_ANCHOR_STEP_CAP = 18; // cap-limited travel: covers ~240×18 px before settle
// Stiff enough that the early pull saturates the step cap (linear travel toward
// the wall) instead of an exponential crawl that never crosses a wide space.
const W_WALL_ANCHOR = 0.25;    // horizontal spring stiffness toward the wall

// ---- Entry point -----------------------------------------------------------

/**
 * Sync solver — drains the step generator and returns the final result.
 * This is the path used by buildBoardFromModules for every space during
 * normal operation.
 */
export function stressLayout(input: StressInput): StressResult {
  const gen = stressLayoutSteps(input);
  let step = gen.next();
  while (!step.done) step = gen.next();
  return step.value;
}

/**
 * Generator that exposes every meaningful step of the solver — one
 * gradient iteration, one placeAtIdeal move, or one ghost-repair sub-step
 * (travel or settle). The caller drives iteration via `.next()`. The sync
 * solver drains it immediately;
 * the animation driver awaits a frame between calls so each step becomes
 * visible.
 *
 * This is the single source of truth for the solver schedule. Both the
 * synchronous and animated paths run through this generator, so their
 * outputs are guaranteed identical up to the number of steps taken.
 */
export function* stressLayoutSteps(
  input: StressInput,
): Generator<StressStep, StressResult, void> {
  const { nodes: inputNodes, edges } = input;
  const rawPorts = input.externalPorts ?? [];
  const rawPrevious = input.previous ?? new Map<string, Rect>();
  const rawPinned = input.pinned ?? new Map<string, Rect>();
  // One resolved budget for every gate below. An omitted budget means
  // "full" EVERYWHERE — previously the SGD gate treated undefined as full
  // while the sand gate treated it as not-full, an inconsistent limbo.
  const budget: "full" | "incremental" | "drag" = input.budget ?? "full";
  const dir: FlowDirection = input.direction ?? "LR";
  // Frame label strip must land on whichever FLOW side maps to SCREEN TOP
  // (the renderer draws it there): LR/RL → flow y-min, TB → flow x-min,
  // BT → flow x-max (screen y = -(flow x + w)).
  const frameLabelSide = dir === "TB" ? ("left" as const) : dir === "BT" ? ("right" as const) : ("top" as const);
  // Dev capture hook (self-arming via localStorage["arch.capture"]="1"):
  // records every solve's EXACT input on globalThis.__ARCH_CAPTURED — the
  // only faithful way to rebuild the headless harness on what the app
  // actually solves (harness-fidelity work; remove after).
  if (readDogfoodFlag("arch.capture") === "1") {
    const g = globalThis as { __ARCH_CAPTURED?: unknown[] };
    (g.__ARCH_CAPTURED ??= []).push({
      nodes: inputNodes,
      edges,
      ports: rawPorts,
      budget,
      dir,
    });
  }
  // Neighborhood solve (P2). A forced partition (frame groups) implies the
  // hood path unconditionally; otherwise explicit input wins; otherwise the
  // dogfood switch decides: in a browser, `localStorage["arch.layout.
  // neighborhoods"] = "1"` turns the two-level solve on for every solve that
  // doesn't set the flag itself (flip in the console, reload). Headless and
  // tests aren't a browser and stay flag-off until the P3 default flip.
  const forcedPartition = input.partition;
  const forcedParents = input.partitionParents;
  const neighborhoods =
    (forcedPartition ? true : undefined) ??
    input.neighborhoods ??
    readNeighborhoodsSwitch();

  // Degenerate cases never see directional forces — solve in screen space.
  if (inputNodes.length === 0) return emptyResult();
  if (inputNodes.length === 1) return singleResult(inputNodes[0]!, rawPinned, rawPrevious);

  // Boundary transform: warm/pinned rects into flow space. Everything from
  // here to packResult thinks in flow coordinates (flow = +x).
  const previous = new Map<string, Rect>();
  for (const [id, r] of rawPrevious) previous.set(id, toFlowRect(r, dir));
  const pinned = new Map<string, Rect>();
  for (const [id, r] of rawPinned) pinned.set(id, toFlowRect(r, dir));

  // Everyone gets a rect; auto-size defaults to NODE_W × NODE_H (screen).
  // Vertical flow swaps every node's w/h in flow space.
  const swapWH = isVertical(dir);
  const size = new Map<string, { w: number; h: number }>();
  for (const n of inputNodes) {
    const w = n.w > 0 ? n.w : NODE_W;
    const h = n.h > 0 ? n.h : NODE_H;
    size.set(n.id, swapWH ? { w: h, h: w } : { w, h });
  }
  for (const [id, r] of pinned) size.set(id, { w: r.w, h: r.h });

  const inputIdSet = new Set(inputNodes.map((n) => n.id));
  const rawCleanEdges = edges.filter(
    (e) => e.from !== e.to && inputIdSet.has(e.from) && inputIdSet.has(e.to),
  );

  // ---- Pre-solved topologies: coarsen motif assemblies -------------------
  // Detected fanout/fan-in formations get their internal geometry from a
  // template (motifs.ts) and enter the solve as ONE super-node — the
  // formation behaves as a single body (every external force moves the
  // whole thing) while its pose stays fully dynamic. Members re-appear at
  // their slots in every emitted result (see `pack` below).
  //
  // A pinned LEAF leaves its wing (the user is rearranging that node) —
  // the rest of the formation stays assembled. A pinned HUB keeps the
  // whole assembly: the pin transfers to the super-node below, so dragging
  // the hub moves the formation as ONE body. External ports on ANY member
  // (hub or leaf) transfer to the super-node — the wall anchor then pulls
  // the body. Dissolving the wing instead (the old rule) traded a good
  // formation for a wall pull: one cross-space caller on a leaf collapsed
  // its whole family to the organic solver. Shelf members with ports DO
  // stay free — a shelf is furniture positioned purely by its callers'
  // springs, and a wall pull on the body would drag every co-shelved sink
  // with it.
  const portNodeIds = new Set(rawPorts.map((p) => p.nodeId));
  const inputTeamOf = new Map<string, string | undefined>();
  for (const n of inputNodes) inputTeamOf.set(n.id, n.team);
  // TRUE-TWIN shelves run BEFORE gateway detection: a lone twin can
  // otherwise anchor a fan-in of the shared callers (upstream territories
  // don't see the downstream sibling), arbitrarily crowning one twin and
  // stranding the rest. Twin members are reserved — real topology for
  // territories/caps, but never wing anchors or leaves.
  const shelfMemberFilter = (s: { memberIds: readonly string[] }) =>
    s.memberIds.filter((l) => !pinned.has(l) && !portNodeIds.has(l));
  // Formations must not straddle a forced group boundary (frame groups): a
  // rigid body spanning two frames would plant one group's members inside
  // the other's rect by construction. Wing leaves filter to the ANCHOR's
  // group; a shelf splits into per-group stacks. Shared by the live
  // pipeline (with `input.partition`) and the edit-calibration baseline
  // (with the PRE-EDIT `editBaseline.partition`).
  const sameGroupLeaves = <M extends { anchorId: string; leafIds: readonly string[] }>(
    m: M,
    part: ReadonlyMap<string, string> | undefined,
  ): M =>
    part
      ? { ...m, leafIds: m.leafIds.filter((l) => part.get(l) === part.get(m.anchorId)) }
      : m;
  const splitShelfByGroup = <S extends { memberIds: readonly string[] }>(
    s: S,
    part: ReadonlyMap<string, string> | undefined,
  ): S[] => {
    if (!part) return [s];
    const byGroup = new Map<string, string[]>();
    for (const id of s.memberIds) {
      const k = part.get(id) ?? "";
      const arr = byGroup.get(k) ?? [];
      arr.push(id);
      byGroup.set(k, arr);
    }
    if (byGroup.size <= 1) return [s];
    return [...byGroup.entries()]
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([, ids]) => ({ ...s, memberIds: ids }));
  };
  const rawTwinShelves = detectShelves(
    inputNodes.map((n) => n.id),
    rawCleanEdges,
    new Set(),
    { exactTwins: true },
  );
  // Reservation comes from the UNFILTERED groups: twinhood is topology,
  // independent of pinning. Deriving it post-filter let an in-place grab
  // of one twin dissolve a 2-group, un-reserve BOTH, and crown the pinned
  // sink as a phantom in-wing over the shared callers — snapping them
  // ~95px into template slots mid-drag. Near-twins (overlapping-but-unequal
  // caller sets) fold in here too — reserved but forming no shelf, so both
  // siblings stay organic instead of one crowning a fan-in over the other.
  const twinReserved = nearTwinReserved(
    inputNodes.map((n) => n.id),
    rawCleanEdges,
    new Set(rawTwinShelves.flatMap((s) => s.memberIds)),
  );
  const twinShelves = rawTwinShelves
    .map((s) => ({ ...s, memberIds: shelfMemberFilter(s) }))
    .flatMap((s) => splitShelfByGroup(s, forcedPartition))
    .filter((s) => s.memberIds.length >= SHELF_MIN_MEMBERS);
  const leafFilteredMotifs = detectMotifs(
    inputNodes.map((n) => n.id),
    rawCleanEdges,
    size,
    twinReserved,
  )
    .map((m) => ({
      ...m,
      leafIds: m.leafIds.filter((l) => !pinned.has(l)),
    }))
    .map((m) => sameGroupLeaves(m, forcedPartition))
    .filter((m) => m.leafIds.length >= 1);
  // Drag frames coarsen ONLY the formation being dragged (pinned hub →
  // it follows the hand as one body). Re-templating unrelated assemblies
  // mid-drag would snap their members visibly while the user is holding
  // something else entirely. A motif whose anchor rides another wing
  // (a swallowed sub-assembly) belongs to its ancestor's body — walk the
  // claim chain so dragging the ancestor keeps the whole flattened
  // formation together. The chain is a forest (see buildAssemblies).
  const claimedBy = new Map<string, string>();
  for (const m of leafFilteredMotifs) {
    for (const l of m.leafIds) claimedBy.set(l, m.anchorId);
  }
  const eligibleMotifs = leafFilteredMotifs.filter((m) => {
    if (budget !== "drag") return true;
    for (
      let cur: string | undefined = m.anchorId;
      cur !== undefined;
      cur = claimedBy.get(cur)
    ) {
      if (pinned.has(cur)) return true;
    }
    return false;
  });
  // Sink shelves run AFTER gateway claiming (claimed = every assembly
  // participant), mirror the pinned/port member filter, and must keep ≥ 2
  // members past it (a pinned member must not leave a 1-node rigid box).
  // Shelves DISSOLVE during drag frames: they have no pinnable body —
  // members hold at their warm rects and re-template on release.
  const gatewayClaimed = new Set<string>([
    ...eligibleMotifs.flatMap((m) => [m.anchorId, ...m.leafIds]),
    ...twinReserved,
  ]);
  // Neighborhood mode forms NO overlap shelves — twins only. The overlap
  // yoke grids sinks whose remaining callers disagree, stranding each
  // ×6-12 from its other caller; a yoke whose pulls agree is a twin class
  // the twin pass already owns (see detectShelves header).
  const eligibleShelves =
    budget === "drag"
      ? []
      : [
          ...twinShelves,
          ...(neighborhoods
            ? []
            : detectShelves(
                inputNodes.map((n) => n.id),
                rawCleanEdges,
                gatewayClaimed,
              )
                .map((s) => ({ ...s, memberIds: shelfMemberFilter(s) }))
                .filter((s) => s.memberIds.length >= SHELF_MIN_MEMBERS)),
        ];
  // Leaves have degree 1 by definition — same ideal-gap the springs
  // would use for this edge (min-degree table row 1 + team bonus).
  const templateIdealGap = (hubId: string, leafId: string): number => {
    const base = IDEAL_BY_MIN_DEGREE[1] ?? IDEAL_CAP;
    const sameTeam =
      inputTeamOf.get(hubId) !== undefined &&
      inputTeamOf.get(hubId) === inputTeamOf.get(leafId);
    return sameTeam ? Math.max(0, base - IDEAL_TEAM_BONUS) : base;
  };
  const assemblies = buildAssemblies(
    eligibleMotifs,
    size,
    templateIdealGap,
    eligibleShelves,
    bandOfPorts(rawPorts),
  );
  // Body anchor for pin/warm-start transfer: gateways anchor on the hub;
  // shelves on the first member (by id) that HAS prior geometry — a cold
  // just-added member as anchor would cold-seed the whole shelf.
  const anchorOf = (a: MotifAssembly, has: (id: string) => boolean): string | undefined =>
    a.kind === "gateway"
      ? a.hubId
      : [...a.memberIds].sort().find(has);
  const memberToSuper = new Map<string, string>();
  const superOf = new Map<string, MotifAssembly>();
  for (const a of assemblies) {
    superOf.set(a.superId, a);
    for (const id of a.memberIds) memberToSuper.set(id, a.superId);
    size.set(a.superId, { w: a.w, h: a.h });
    // A pinned hub pins the whole assembly — the formation follows the
    // user's hand as one body. Shelves have no pinnable body: pinned
    // members were already filtered out of membership above.
    if (a.kind === "gateway") {
      const hubOffset = a.offsets.get(a.hubId)!;
      const hubPin = pinned.get(a.hubId);
      if (hubPin) {
        pinned.set(a.superId, {
          x: hubPin.x - hubOffset.dx,
          y: hubPin.y - hubOffset.dy,
          w: a.w,
          h: a.h,
        });
        pinned.delete(a.hubId);
      }
    }
    // Warm start: the anchor member's previous rect places the whole
    // assembly (hub for gateways, first member with prior geometry for
    // shelves).
    const warmAnchor = anchorOf(a, (id) => previous.has(id));
    const anchorPrev = warmAnchor ? previous.get(warmAnchor) : undefined;
    if (warmAnchor && anchorPrev) {
      const o = a.offsets.get(warmAnchor)!;
      previous.set(a.superId, {
        x: anchorPrev.x - o.dx,
        y: anchorPrev.y - o.dy,
        w: a.w,
        h: a.h,
      });
    }
    for (const id of a.memberIds) previous.delete(id);
  }

  // Standing formation force: the organize DECISION (columns/stacks),
  // derived deterministically from the current state and enforced as
  // anchor springs in EVERY phase that runs physics. One force system
  // everywhere — the settled equilibrium of a cold solve is therefore
  // still an equilibrium during drags and warm solves ("add forces,
  // never remove"). For warm budgets the decision is made at entry from
  // the standing layout; for full solves it is re-made after the global
  // phases (see the settle block) once a real shape exists.
  const formationTargets = new Map<string, { x: number; y: number }>();
  const nodes: StressNode[] = [
    ...inputNodes.filter((n) => !memberToSuper.has(n.id)),
    ...assemblies.map((a) => {
      const s = size.get(a.superId)!;
      // Gateways inherit the hub's team. A shelf takes its first member's
      // team only when ALL members agree — a mixed shelf has no honest
      // team and gets none.
      const team =
        a.kind === "gateway"
          ? inputTeamOf.get(a.hubId)
          : a.memberIds.every(
                (m) => inputTeamOf.get(m) === inputTeamOf.get(a.memberIds[0]!),
              )
            ? inputTeamOf.get(a.memberIds[0]!)
            : undefined;
      return { id: a.superId, w: s.w, h: s.h, team };
    }),
  ];
  const ports: StressPort[] = rawPorts.map((p) =>
    memberToSuper.has(p.nodeId)
      ? { ...p, nodeId: memberToSuper.get(p.nodeId)! }
      : p,
  );

  // Indexes --------------------------------------------------------------
  const nodeIds = nodes.map((n) => n.id);
  const idSet = new Set(nodeIds);
  // Coarse edges remember their RAW endpoints: a spring into an assembly
  // can then anchor at the member's SLOT instead of the body centre (see
  // slot anchors below) — the coarse graph is otherwise blind to which
  // side of a big body the peer actually connects to.
  const cleanEdges: CoarseEdge[] = rawCleanEdges
    .map((e) => ({
      from: memberToSuper.get(e.from) ?? e.from,
      to: memberToSuper.get(e.to) ?? e.to,
      origFrom: e.from,
      origTo: e.to,
    }))
    .filter((e) => e.from !== e.to && idSet.has(e.from) && idSet.has(e.to));

  // Intra-SCC edges carry no defensible left→right order — exempt from
  // BOTH flow mechanisms: the SGD inequality terms (below) AND the
  // constant W_FLOW gradient bias applied every stepGradient round. On a
  // pure cycle the bias contributions cancel, but any chord inside the
  // SCC breaks the balance and shears the ring sideways.
  const orderExemptKeys = intraSccEdgeKeys(
    nodeIds,
    cleanEdges,
  );
  // Neighborhood jurisdictions (P2, flag-gated). Partitioned on the RAW
  // topology (R1: never pin-, port-, or budget-filtered inputs) and
  // projected onto the coarse graph via assembly membership — every
  // assembly is a formation seed, so its members share one hood and the
  // projection is well-defined. Raw partitioning keeps every edge's
  // intra/cross classification identical across budgets: a drag frame
  // coarsens only the dragged formation, and a coarse-graph partition
  // would flip classifications mid-gesture (bias and boost switching on
  // and off under the pointer). Flow rank order only exists BETWEEN
  // neighborhoods, and only where the hood graph itself is acyclic —
  // hood pairs inside a level-2 SCC are exempt from the inequalities AND
  // from the constant W_FLOW bias (the node-level rationale carries: a
  // chord inside a quotient cycle shears the ring sideways; keeping the
  // bias on L2 pairs measured +27 crossings / +11 node-edge crossings on
  // the root-graph fixture with zero render impact elsewhere).
  // R6 hysteresis: an edit solve partitions with the PRE-EDIT topology's
  // own partition as `previous` — a node's district survives the edit
  // unless the new pull outweighs it past the hysteresis band. Non-edit
  // solves see unchanged topology, where the deterministic cold
  // partition is already stable (and memoized) without it.
  const prevHoods: Map<string, string> | undefined =
    neighborhoods && input.editBaseline && input.trimBase
      ? (() => {
          const prevSizes = new Map<string, { w: number; h: number }>();
          for (const [id, r] of input.trimBase) {
            const fr = toFlowRect(r, dir);
            prevSizes.set(id, { w: fr.w, h: fr.h });
          }
          // The pre-edit world's own partition: pre-edit FORCED assignment
          // (editBaseline.partition) + topology remainder — same rule the
          // baseline hood world applies below.
          return buildRawHood({
            rawIds: [...input.trimBase.keys()],
            rawEdges: input.editBaseline.edges,
            sizes: prevSizes,
            ...(input.editBaseline.partition
              ? { forced: input.editBaseline.partition }
              : {}),
          });
        })()
      : undefined;
  const hoodWorld = neighborhoods
    ? buildHoodMaps({
        coarseIds: nodeIds,
        rawIds: inputNodes.map((n) => n.id),
        rawEdges: rawCleanEdges,
        sizes: size,
        coarseEdges: cleanEdges,
        assemblyOf: (id) => superOf.get(id),
        ...(prevHoods ? { previous: prevHoods } : {}),
        ...(forcedPartition ? { forced: forcedPartition } : {}),
      })
    : undefined;
  const hoodOf = hoodWorld?.hoodOf;
  /** Intra-hood, or cross-hood inside a level-2 SCC — no flow order. */
  const hoodExempt = (e: { from: string; to: string }): boolean => {
    if (!hoodWorld) return false;
    const ha = hoodWorld.hoodOf.get(e.from);
    const hb = hoodWorld.hoodOf.get(e.to);
    if (ha === hb) return true; // intra-neighborhood: proximity rules only
    return hoodWorld.hoodPairExempt.has(`${ha}|${hb}`);
  };
  /** True when this directed edge must carry NO flow-rank inequality.
   *  Intra-hood edges are proximity-only — EXCEPT when an endpoint is a
   *  rigid assembly: a formation body is the structure layer, and structure
   *  obeys reading rules. Without this, a wing brick and its in-hood peer
   *  have no ordering at all and the brick parks anti-flow wherever
   *  repulsion shoves it (measured: the Customer fan settled BELOW-RIGHT of
   *  the gateway it feeds, fe→gw stretch ×8.1). Organic intra-hood edges
   *  stay exempt — hard-ordering the core hood's multi-caller sinks is the
   *  laddering failure the exemption exists for. SGD-only (cold solves):
   *  no baseline-mirror term, calibration never sees inequalities. */
  const touchesAssembly = (e: { from: string; to: string }): boolean =>
    superOf.has(e.from) || superOf.has(e.to);
  const flowExempt = (e: { from: string; to: string }): boolean =>
    orderExemptKeys.has(`${e.from}|${e.to}`) ||
    (hoodExempt(e) && !touchesAssembly(e));
  const biasCache = new Map<readonly StressEdge[], StressEdge[]>();
  const biasEdgesOf = (list: readonly StressEdge[]): StressEdge[] => {
    // Global solve: the constant nudge applies to EVERY edge, intra-SCC
    // included. Stripping SCC edges was tried ("chords shear the ring")
    // and reverted on the era-R3 reference render: on the real board the
    // intra-core bias is part of what spreads the entangled centre, and
    // removing it carpeted the core while the small-fixture metrics saw
    // nothing. Neighborhood mode scopes instead of stripping: the nudge
    // is a BETWEEN-hoods force — inside a hood even ±W_FLOW ladders
    // multi-caller sinks (measured ×7 on the demo core hood).
    if (!hoodOf) return list as StressEdge[];
    let out = biasCache.get(list);
    if (!out) {
      out = list.filter(
        (e) => !orderExemptKeys.has(`${e.from}|${e.to}`) && !hoodExempt(e),
      );
      biasCache.set(list, out);
    }
    return out;
  };

  // One force world for the current graph: degrees, density census, per-
  // edge springs, connected/sibling pair sets. The same builder constructs
  // the PRE-EDIT world during edit-gesture calibration (see below), so both
  // worlds measure with identical rules.
  const teamOf = new Map<string, string | undefined>();
  for (const n of nodes) teamOf.set(n.id, n.team);
  const world = buildForceWorld(nodeIds, cleanEdges, teamOf);
  // Neighborhood mode: cross-hood springs carry the macro cohesion the
  // plan's R3 assigns to the condensed graph — hub damping otherwise
  // leaves a lone edge between two hoods too weak against organize
  // clearances and repulsion, stranding whole neighborhoods (measured
  // deg-1 stretch ×6+). Boost is per-EDGE, so multi-edge hood pairs pull
  // proportionally harder.
  const CROSS_HOOD_STIFFNESS_BOOST = 3;
  // Slot anchors (neighborhood mode): a spring into an assembly anchors at
  // the connected MEMBER's slot, measured with the member's own silhouette.
  // Body-centre springs are blind to which side the peer connects to — a
  // caller of a wing leaf parks rect-adjacent at the body's far corner and
  // still reads ×9 stretched to its actual peer (measured: Customer vs the
  // fe wing). The gradient still lands on the body as a whole.
  const slotAnchorOf = (
    superId: string,
    memberId: string | undefined,
  ): SlotAnchor | undefined => {
    if (memberId === undefined || memberId === superId) return undefined;
    const a = superOf.get(superId);
    const o = a?.offsets.get(memberId);
    const s = size.get(memberId);
    if (!o || !s) return undefined;
    return { dx: o.dx + s.w / 2, dy: o.dy + s.h / 2, w: s.w, h: s.h };
  };
  const edgeForces = hoodOf
    ? world.edgeForces.map((f) => {
        const fromAnchor = slotAnchorOf(f.from, f.origFrom);
        const toAnchor = slotAnchorOf(f.to, f.origTo);
        const boosted =
          hoodOf.get(f.from) !== hoodOf.get(f.to)
            ? f.stiffness * CROSS_HOOD_STIFFNESS_BOOST
            : f.stiffness;
        if (!fromAnchor && !toAnchor && boosted === f.stiffness) return f;
        return {
          ...f,
          stiffness: boosted,
          ...(fromAnchor ? { fromAnchor } : {}),
          ...(toAnchor ? { toAnchor } : {}),
        };
      })
    : world.edgeForces;
  const { deg, connectedSet, siblingSet, crowdOf } = world;
  // Crowd-scaled separation floor (loop round 1): dense-mesh members get
  // a bigger GUARANTEED clearance — spring pads alone can't deliver it,
  // the core compresses to whatever this floor is.
  const sepPadOf = (id: string): number =>
    Math.min(
      SEP_CROWD_MAX,
      SEP_PER_CROWD * Math.max(0, (crowdOf.get(id) ?? 0) - CROWD_BASE),
    );

  // Frame groups: the forced partition projected onto COARSE ids — an
  // assembly takes its representative's group (the formation filter keeps
  // assemblies single-group). Feeds cohesion (main rounds + calibration
  // mirror) and the frame-separation phase.
  const coarseGroupOf = (
    forced: ReadonlyMap<string, string>,
    ids: readonly string[],
    asmOf: (id: string) => MotifAssembly | undefined,
  ): Map<string, string> => {
    const m = new Map<string, string>();
    for (const id of ids) {
      const a = asmOf(id);
      const rep = a
        ? a.kind === "gateway"
          ? a.hubId
          : [...a.memberIds].sort()[0]!
        : id;
      const g = forced.get(rep);
      if (g !== undefined) m.set(id, g);
    }
    return m;
  };
  const frameGroupOfCoarse = forcedPartition
    ? coarseGroupOf(forcedPartition, nodeIds, (id) => superOf.get(id))
    : undefined;

  // Component decomposition --------------------------------------------
  // Split into weakly-connected components. Each CC solves independently:
  // springs, flow, parent gravity, all-pairs repulsion all scoped to CC
  // members. Singletons (degree-zero) form trivial 1-node CCs and skip the
  // solver entirely (no internal forces). `packComponents` then shelf-packs
  // CC bounding boxes so they don't overlap on canvas.
  const ccs = findComponents(nodes, cleanEdges, edgeForces, ports, pinned, previous);

  // Seed positions per CC. Each CC is seeded in its own local frame (around
  // origin for cold CCs). For mixed CCs (some warm/pinned, some cold), the
  // cold seeds are translated by the centroid of anchor positions so all
  // CC members start near each other instead of warm at absolute coords +
  // cold at origin.
  const pos = new Map<string, { x: number; y: number }>();
  for (const cc of ccs) {
    const partial = seedPositions(cc.nodes, cc.directedEdges, previous, pinned);
    let cx = 0, cy = 0, anchorCount = 0, coldCount = 0;
    for (const n of cc.nodes) {
      if (pinned.has(n.id) || previous.has(n.id)) {
        const p = partial.get(n.id)!;
        cx += p.x; cy += p.y; anchorCount++;
      } else {
        coldCount++;
      }
    }
    // Anchor offset only applies in multi-CC graphs. Single-CC graphs keep
    // their original cold-at-origin seed so byte-for-byte equality with the
    // pre-CC-decomposition behaviour is preserved (the old solver pulled
    // cold seeds toward warm peers via springs from origin).
    if (ccs.length >= 2 && anchorCount > 0 && coldCount > 0) {
      cx /= anchorCount; cy /= anchorCount;
      for (const n of cc.nodes) {
        if (!pinned.has(n.id) && !previous.has(n.id)) {
          const p = partial.get(n.id)!;
          p.x += cx; p.y += cy;
        }
      }
    }
    for (const [k, v] of partial) pos.set(k, v);
  }

  // Pre-pack: distribute cold CCs across the canvas so the solver starts
  // with each CC in a distinct slot. Parent gravity then pulls each CC
  // toward its own slot centroid instead of all CCs piling at origin.
  packComponents(ccs, pos, size);

  // Global stress phase (full solves only) — S_GD2 over graph-target
  // distances, with the directed-flow ordering constraints in the same
  // annealed pair stream. This is what gives dense graphs a faithful
  // global shape; the spring rounds below then polish local spacing on
  // top of it. Warm/incremental solves skip it — their global shape came
  // from a previous full solve and must stay put.
  if (budget === "full") {
    for (const cc of ccs) {
      if (cc.ids.length < 2) continue;
      const sortedIds = [...cc.ids].sort();
      const idxOf = new Map<string, number>();
      sortedIds.forEach((id, i) => idxOf.set(id, i));
      const pairs = buildTargetDistances(sortedIds, cc.edges, size);
      // Ideal length per directed pair (for the flow separation scale).
      const idealByKey = new Map<string, number>();
      for (const ef of cc.edges) idealByKey.set(`${ef.from}|${ef.to}`, ef.idealLen);
      // SCCs never span connected components, so the global exempt set
      // restricted to this CC's edges is exactly the per-CC computation.
      const flow: FlowTerm[] = [];
      for (const e of cc.directedEdges) {
        if (e.from === e.to) continue;
        if (flowExempt(e)) continue;
        const ideal = idealByKey.get(`${e.from}|${e.to}`) ?? IDEAL_CAP;
        const len =
          ideal + meanRadius(size.get(e.from)!) + meanRadius(size.get(e.to)!);
        flow.push({
          i: idxOf.get(e.from)!,
          j: idxOf.get(e.to)!,
          sep: FLOW_SEP_FRAC * len,
          w: 1 / (len * len),
        });
      }
      const gen = sgdPhase({
        ids: sortedIds,
        pos,
        size,
        pinned,
        pairs,
        flow,
        seed: hashIds(sortedIds),
        chunk: 200,
      });
      let s = gen.next();
      while (!s.done) {
        yield { kind: "sgd", round: 0, result: packResult(nodeIds, pos, size, dir, rawPinned, assemblies) };
        s = gen.next();
      }
    }
  }

  // Iterate --------------------------------------------------------------
  // Converge-repair loop: each round does a gradient-descent pass followed
  // by a structural repair pass. Teleports in repair create large local
  // disturbances that the next round's gradient descent smooths out, and
  // the following repair often finds *different* long edges that couldn't
  // be fixed before because the first move had to happen first.
  const isDrag = budget === "drag";
  const rounds = isDrag
    ? 1
    : budget === "incremental"
      ? ROUNDS_INCREMENTAL
      : ROUNDS_FULL;
  const iterPerRound = isDrag
    ? ITER_DRAG
    : budget === "incremental"
      ? ITER_PER_ROUND_INCREMENTAL
      : ITER_PER_ROUND_FULL;

  // Model-edit gesture: pre-edit topology supplied for an incremental
  // solve. The edit's deltas must ACT — so friction (which would freeze
  // sub-threshold deltas) is off, the rounds run, and trim anchors are
  // calibrated against the pre-edit world below. Everything else responds
  // only through the anchors: hold by construction, not by freezing.
  const editSolve =
    budget === "incremental" &&
    input.editBaseline !== undefined &&
    (input.trimBase?.size ?? 0) > 0;

  // Sand only applies once positions are warm — never during a cold full
  // solve, which would jam under friction. Drag + incremental are the
  // "user-facing" budgets where sand belongs.
  const sand = input.mode === "sand" && budget !== "full" && !editSolve;
  // A sand+incremental solve is a MAINTENANCE solve (same-topology warm
  // recompute): hold the standing layout, skip the float rounds + rest.
  const maintenance = sand && budget === "incremental";

  // Solver scope per CC: only CCs with ≥2 members have any internal forces
  // to compute. Singletons get their position from pre-pack (or anchored)
  // and contribute nothing to dynamics. Drag mode further narrows to the
  // CC containing the dragged (pinned) node — other CCs stay frozen so a
  // drag never disturbs unrelated subgraphs.
  let solverCCs = ccs.filter((cc) => cc.ids.length >= 2);
  if (isDrag && pinned.size > 0) {
    solverCCs = solverCCs.filter((cc) =>
      cc.ids.some((id) => pinned.has(id)),
    );
  }
  let solverScope = 0;
  for (const cc of solverCCs) solverScope += cc.ids.length;

  // Gesture baseline, boundary-transformed. `rawTrimBase` keeps the as-
  // rendered granularity (assembly members individually — needed to
  // reconstruct the PRE-EDIT coarse world during edit calibration);
  // `trimBase` is the current-coarse view (supers derived from their
  // hub's base rect), remapped like `previous`.
  const rawTrimBase = new Map<string, Rect>();
  const trimBase = new Map<string, Rect>();
  if (input.trimBase) {
    for (const [id, r] of input.trimBase) {
      const fr = toFlowRect(r, dir);
      rawTrimBase.set(id, fr);
      trimBase.set(id, fr);
    }
    for (const a of assemblies) {
      // Anchor rule matches warm-start: hub for gateways, first member
      // (by id) WITH a base rect for shelves — a shelf super without a
      // trim anchor would drift unmoored through edit solves.
      const anchor = anchorOf(a, (id) => trimBase.has(id));
      const anchorBase = anchor ? trimBase.get(anchor) : undefined;
      if (anchor && anchorBase) {
        const o = a.offsets.get(anchor)!;
        trimBase.set(a.superId, {
          x: anchorBase.x - o.dx,
          y: anchorBase.y - o.dy,
          w: a.w,
          h: a.h,
        });
      }
      for (const id of a.memberIds) trimBase.delete(id);
    }
  }

  // Pre-edit topology projected into the CURRENT coarse id domain — used
  // only to DIFF against the current graph (edit scope below). The
  // calibration itself uses the baseline's own coarsening instead (see
  // the trim-anchor block).
  let baseEdgesCur: StressEdge[] = [];
  let basePortsCur: StressPort[] = [];
  if (editSolve) {
    const baseIdSet = new Set(trimBase.keys());
    baseEdgesCur = input.editBaseline!.edges
      .map((e) => ({
        from: memberToSuper.get(e.from) ?? e.from,
        to: memberToSuper.get(e.to) ?? e.to,
      }))
      .filter(
        (e) => e.from !== e.to && baseIdSet.has(e.from) && baseIdSet.has(e.to),
      );
    basePortsCur = (input.editBaseline!.ports ?? rawPorts).map((p) =>
      memberToSuper.has(p.nodeId)
        ? { ...p, nodeId: memberToSuper.get(p.nodeId)! }
        : p,
    );
  }

  // Edit response channels. Two distinct sets:
  //
  // `editFree` — the DECISIVE movers: spawned nodes plus, per ADDED edge,
  // its less-constrained endpoint (lower current degree; ties to smaller
  // id — the same preference ghost repair uses). These nodes get NO trim
  // anchor (their job is to relocate meaningfully) and are the only
  // candidates the structural repairs may move. Everything else responds
  // solely through anchored force deltas: resize pushes neighbours the
  // exact growth amount, a removed edge/node releases its pull, and the
  // untouched rest holds.
  //
  // `editWallScope` — nodes whose EXTERNAL PORTS the edit changed: the
  // only ones the wall-anchor phase may re-anchor during an edit solve.
  let editFree: Set<string> | undefined;
  let editWallScope: Set<string> | undefined;
  if (editSolve) {
    editFree = new Set();
    for (const id of nodeIds) {
      if (!previous.has(id) && !pinned.has(id)) editFree.add(id);
    }
    const keyOf = (e: StressEdge) => `${e.from}|${e.to}`;
    const baseKeys = new Set(baseEdgesCur.map(keyOf));
    for (const e of cleanEdges) {
      if (baseKeys.has(keyOf(e))) continue;
      // Added edge. A cold endpoint is already free and IS the response —
      // the warm one stays anchored (spawning a leaf must not unmoor its
      // hub). Both warm: free the less-constrained endpoint.
      const fromCold = !previous.has(e.from) && !pinned.has(e.from);
      const toCold = !previous.has(e.to) && !pinned.has(e.to);
      if (fromCold || toCold) continue;
      const fromOk = !pinned.has(e.from);
      const toOk = !pinned.has(e.to);
      if (!fromOk && !toOk) continue;
      let mover: string;
      if (!fromOk) mover = e.to;
      else if (!toOk) mover = e.from;
      else {
        const dFrom = deg.get(e.from) ?? 0;
        const dTo = deg.get(e.to) ?? 0;
        if (dFrom < dTo) mover = e.from;
        else if (dTo < dFrom) mover = e.to;
        else mover = e.from < e.to ? e.from : e.to;
      }
      editFree.add(mover);
    }
    editWallScope = new Set();
    const portSig = (ps: readonly StressPort[]): Map<string, string> => {
      const acc = new Map<string, string[]>();
      for (const p of ps) {
        const k = `${p.direction}|${p.peerId ?? ""}`;
        const arr = acc.get(p.nodeId);
        if (arr) arr.push(k);
        else acc.set(p.nodeId, [k]);
      }
      const out = new Map<string, string>();
      for (const [id, arr] of acc) out.set(id, arr.sort().join(","));
      return out;
    };
    const curPortSig = portSig(ports);
    const basePortSig = portSig(basePortsCur);
    for (const id of nodeIds) {
      if ((curPortSig.get(id) ?? "") !== (basePortSig.get(id) ?? "")) {
        editWallScope.add(id);
      }
    }
  }

  // TRIM ANCHORS (warm budgets): measure the standing gradient at entry
  // and anchor every warm node at t = x + g/K — the position that makes
  // the standing layout an EXACT equilibrium of (base forces + anchors).
  // No target re-derivation, no ratchet: a frame that changes nothing
  // ends exactly where it began, and interaction acts as force DELTAS
  // against the trim field ("add forces, never remove").
  if (budget !== "full" && !(sand && budget === "incremental")) {
    if (editSolve) {
      // MODEL-EDIT calibration: measure the PRE-EDIT world — old
      // positions, old sizes, old edges/census, old ports — so the
      // standing forces of the graph the user was looking at are what
      // gets absorbed. The current solve then plays the edit's delta
      // forces in full: a new edge pulls, a removed one releases, a
      // resize pushes. Nodes the edit removed still exist here as
      // phantom force sources (rects come from trimBase); their absence
      // from the live solve is thereby a genuine release delta.
      //
      // The baseline is coarsened with ITS OWN assemblies (motifs
      // detected on the old edges/sizes), reproducing the exact coarse
      // world the previous solve settled in. Coarsening it with the
      // CURRENT assemblies instead fabricates geometry that never
      // existed — a re-templated super box at an old hub pose overlaps
      // old rects, floor-level strong repulsion fires, and the
      // calibrated anchors get flung thousands of px.
      const baseRawIds = [...rawTrimBase.keys()];
      const baseRawIdSet = new Set(baseRawIds);
      const baseRawEdges = input.editBaseline!.edges.filter(
        (e) =>
          e.from !== e.to &&
          baseRawIdSet.has(e.from) &&
          baseRawIdSet.has(e.to),
      );
      const baseRawPorts = (input.editBaseline!.ports ?? rawPorts).filter(
        (p) => baseRawIdSet.has(p.nodeId),
      );
      const baseSizeRaw = new Map<string, { w: number; h: number }>();
      for (const [id, r] of rawTrimBase) {
        baseSizeRaw.set(id, { w: r.w, h: r.h });
      }
      // Same eligibility rules as the live coarsening above (pins don't
      // change during an edit; old ports come from the baseline). Pins are
      // checked against the RAW input map — the live `pinned` has already
      // been coarse-remapped (a pinned hub's pin moved to its super id and
      // the raw id deleted), so consulting it would readmit a node the
      // previous solve excluded from wings and fabricate a baseline
      // formation that never existed (whose super box overlaps real
      // baseline rects → floor-level repulsion → flung anchors).
      const oldPortIds = new Set(baseRawPorts.map((p) => p.nodeId));
      // Baseline coarsening mirrors the live pipeline exactly (twin
      // shelves → gateways → overlap shelves) — the editBaseline is
      // latched from a settled solve that used the same three phases.
      const oldShelfMemberFilter = (s: { memberIds: readonly string[] }) =>
        s.memberIds.filter((l) => !rawPinned.has(l) && !oldPortIds.has(l));
      // Reservation from UNFILTERED groups — mirrors the live pipeline
      // (twinhood is topology, independent of pinning).
      const oldRawTwinShelves = detectShelves(
        baseRawIds,
        baseRawEdges,
        new Set(),
        { exactTwins: true },
      );
      const oldTwinReserved = nearTwinReserved(
        baseRawIds,
        baseRawEdges,
        new Set(oldRawTwinShelves.flatMap((s) => s.memberIds)),
      );
      const oldTwinShelves = oldRawTwinShelves
        .map((s) => ({ ...s, memberIds: oldShelfMemberFilter(s) }))
        .flatMap((s) => splitShelfByGroup(s, input.editBaseline?.partition))
        .filter((s) => s.memberIds.length >= SHELF_MIN_MEMBERS);
      const oldMotifs = detectMotifs(
        baseRawIds,
        baseRawEdges,
        baseSizeRaw,
        oldTwinReserved,
      )
        .map((m) => ({
          ...m,
          leafIds: m.leafIds.filter((l) => !rawPinned.has(l)),
        }))
        .map((m) => sameGroupLeaves(m, input.editBaseline?.partition))
        .filter((m) => m.leafIds.length >= 1);
      const oldShelves = [
        ...oldTwinShelves,
        ...(neighborhoods
          ? []
          : detectShelves(
              baseRawIds,
              baseRawEdges,
              new Set([
                ...oldMotifs.flatMap((m) => [m.anchorId, ...m.leafIds]),
                ...oldTwinReserved,
              ]),
            )
              .map((s) => ({ ...s, memberIds: oldShelfMemberFilter(s) }))
              .filter((s) => s.memberIds.length >= SHELF_MIN_MEMBERS)),
      ];
      const oldAssemblies = buildAssemblies(
        oldMotifs,
        baseSizeRaw,
        templateIdealGap,
        oldShelves,
        bandOfPorts(baseRawPorts),
      );
      const oldMemberToSuper = new Map<string, string>();
      // Old assemblies are matched to current ones by superId — the id is
      // deterministic per (kind, anchor), so `~motif~X` and `~shelf~X`
      // never collide even when one node both anchors a gateway and
      // patronizes a shelf.
      const oldSuperByKey = new Map<string, MotifAssembly>();
      const oldSuperAnchor = new Map<string, string>();
      const basePos = new Map<string, { x: number; y: number }>();
      const baseSize = new Map<string, { w: number; h: number }>();
      const baseTeamOf = new Map<string, string | undefined>();
      for (const [id, r] of rawTrimBase) {
        basePos.set(id, { x: r.x, y: r.y });
        baseSize.set(id, { w: r.w, h: r.h });
        baseTeamOf.set(id, inputTeamOf.get(id));
      }
      for (const a of oldAssemblies) {
        // Every baseline member has a rawTrimBase rect by construction,
        // so the anchor always resolves (hub for gateways, first member
        // for shelves).
        const anchor = anchorOf(a, (id) => rawTrimBase.has(id))!;
        const anchorBase = rawTrimBase.get(anchor)!;
        oldSuperByKey.set(a.superId, a);
        oldSuperAnchor.set(a.superId, anchor);
        for (const id of a.memberIds) oldMemberToSuper.set(id, a.superId);
        const o = a.offsets.get(anchor)!;
        basePos.set(a.superId, {
          x: anchorBase.x - o.dx,
          y: anchorBase.y - o.dy,
        });
        baseSize.set(a.superId, { w: a.w, h: a.h });
        baseTeamOf.set(
          a.superId,
          a.kind === "gateway"
            ? inputTeamOf.get(a.hubId)
            : a.memberIds.every(
                  (m) =>
                    inputTeamOf.get(m) === inputTeamOf.get(a.memberIds[0]!),
                )
              ? inputTeamOf.get(a.memberIds[0]!)
              : undefined,
        );
        for (const id of a.memberIds) {
          basePos.delete(id);
          baseSize.delete(id);
        }
      }
      const baseIds = [...basePos.keys()];
      const baseCoarseEdges: CoarseEdge[] = baseRawEdges
        .map((e) => ({
          from: oldMemberToSuper.get(e.from) ?? e.from,
          to: oldMemberToSuper.get(e.to) ?? e.to,
          origFrom: e.from,
          origTo: e.to,
        }))
        .filter((e) => e.from !== e.to);
      const baseCoarsePorts: StressPort[] = baseRawPorts.map((p) =>
        oldMemberToSuper.has(p.nodeId)
          ? { ...p, nodeId: oldMemberToSuper.get(p.nodeId)! }
          : p,
      );
      // Mirror of the live exemptions (orderExemptKeys + hoodExempt) and
      // of the live edge post-map (cross-hood stiffness boost + slot
      // anchors): the pre-edit world must measure with identical rules —
      // any unmirrored force reads as a phantom edit response through
      // the trim anchors (measured: a NO-OP edit drifted 1.9px flag-on
      // when the baseline kept the intra-hood W_FLOW bias the live
      // solve exempts).
      const baseOrderExempt = intraSccEdgeKeys(baseIds, baseCoarseEdges);
      const baseHoodWorld = neighborhoods
        ? buildHoodMaps({
            coarseIds: baseIds,
            rawIds: baseRawIds,
            rawEdges: baseRawEdges,
            sizes: baseSizeRaw,
            coarseEdges: baseCoarseEdges,
            assemblyOf: (id) => oldSuperByKey.get(id),
            ...(input.editBaseline?.partition
              ? { forced: input.editBaseline.partition }
              : {}),
          })
        : undefined;
      const baseHoodExempt = (e: { from: string; to: string }): boolean => {
        if (!baseHoodWorld) return false;
        const ha = baseHoodWorld.hoodOf.get(e.from);
        const hb = baseHoodWorld.hoodOf.get(e.to);
        if (ha === hb) return true;
        return baseHoodWorld.hoodPairExempt.has(`${ha}|${hb}`);
      };
      // Mirrors biasEdgesOf exactly: unfiltered in the global solve,
      // exemptions only in neighborhood mode.
      const baseBiasEdges = baseHoodWorld
        ? baseCoarseEdges.filter(
            (e) =>
              !baseOrderExempt.has(`${e.from}|${e.to}`) && !baseHoodExempt(e),
          )
        : baseCoarseEdges;
      const baseWorld = buildForceWorld(baseIds, baseCoarseEdges, baseTeamOf);
      const baseEdgeForces = baseHoodWorld
        ? (() => {
            const baseHoodOf = baseHoodWorld.hoodOf;
            const baseSlotAnchorOf = (
              superId: string,
              memberId: string | undefined,
            ): SlotAnchor | undefined => {
              if (memberId === undefined || memberId === superId) return undefined;
              const a = oldSuperByKey.get(superId);
              const o = a?.offsets.get(memberId);
              const s = baseSizeRaw.get(memberId);
              if (!o || !s) return undefined;
              return { dx: o.dx + s.w / 2, dy: o.dy + s.h / 2, w: s.w, h: s.h };
            };
            return baseWorld.edgeForces.map((f) => {
              const fromAnchor = baseSlotAnchorOf(f.from, f.origFrom);
              const toAnchor = baseSlotAnchorOf(f.to, f.origTo);
              const boosted =
                baseHoodOf.get(f.from) !== baseHoodOf.get(f.to)
                  ? f.stiffness * CROSS_HOOD_STIFFNESS_BOOST
                  : f.stiffness;
              if (!fromAnchor && !toAnchor && boosted === f.stiffness) return f;
              return {
                ...f,
                stiffness: boosted,
                ...(fromAnchor ? { fromAnchor } : {}),
                ...(toAnchor ? { toAnchor } : {}),
              };
            });
          })()
        : baseWorld.edgeForces;

      // Attach every baseline body to a current solver CC: by its own
      // current location when it still exists (an old super locates via
      // its hub), else — phantoms, dissolved formations of removed hubs —
      // by majority vote of its baseline peers' locations. Ties break
      // toward the earlier CC; bodies with no located peer are dropped
      // (their forces acted only on other vanished bodies).
      //
      // Known approximation: calibration is scoped per CURRENT CC, so a
      // baseline force spanning a post-edit CC boundary (bridge-edge or
      // cut-vertex removal) is measured on at most ONE side — the other
      // side holds exactly instead of leaning into the release. The lost
      // response is the released standing force / W_TRIM (~0.2–2px at a
      // settled equilibrium); cross-CC coupling isn't worth that.
      const ccIndexOf = new Map<string, number>();
      solverCCs.forEach((cc, i) => {
        for (const id of cc.ids) ccIndexOf.set(id, i);
      });
      const locOf = (baseId: string): string | undefined => {
        const raw = oldSuperAnchor.get(baseId) ?? baseId;
        const cur = memberToSuper.get(raw) ?? raw;
        return idSet.has(cur) ? cur : undefined;
      };
      const ccOfBase = new Map<string, number>();
      const unattached: string[] = [];
      for (const id of baseIds) {
        const loc = locOf(id);
        const i = loc !== undefined ? ccIndexOf.get(loc) : undefined;
        if (i !== undefined) ccOfBase.set(id, i);
        else unattached.push(id);
      }
      const basePeersOf = new Map<string, string[]>();
      const notePeer = (a: string, b: string) => {
        const arr = basePeersOf.get(a);
        if (arr) arr.push(b);
        else basePeersOf.set(a, [b]);
      };
      for (const e of baseCoarseEdges) {
        notePeer(e.from, e.to);
        notePeer(e.to, e.from);
      }
      for (const id of unattached) {
        const votes = new Map<number, number>();
        for (const p of basePeersOf.get(id) ?? []) {
          const i = ccOfBase.get(p);
          if (i !== undefined) votes.set(i, (votes.get(i) ?? 0) + 1);
        }
        let best = -1;
        let bestVotes = 0;
        for (const [i, v] of votes) {
          if (v > bestVotes || (v === bestVotes && best !== -1 && i < best)) {
            best = i;
            bestVotes = v;
          }
        }
        if (best !== -1) ccOfBase.set(id, best);
      }

      const sameMembers = (
        a: readonly string[],
        b: readonly string[],
      ): boolean => {
        if (a.length !== b.length) return false;
        const sa = [...a].sort();
        const sb = [...b].sort();
        for (let i = 0; i < sa.length; i++) if (sa[i] !== sb[i]) return false;
        return true;
      };

      for (let i = 0; i < solverCCs.length; i++) {
        const cc = solverCCs[i]!;
        const ccBase: string[] = [];
        for (const id of baseIds) if (ccOfBase.get(id) === i) ccBase.push(id);
        if (ccBase.length === 0) continue;
        const ccBaseSet = new Set(ccBase);
        const collected = new Map<string, { x: number; y: number }>();
        stepGradient({
          nodeIds: ccBase,
          pos: basePos,
          size: baseSize,
          pinned,
          edges: baseEdgeForces.filter(
            (e) => ccBaseSet.has(e.from) && ccBaseSet.has(e.to),
          ),
          directedEdges: baseBiasEdges.filter(
            (e) => ccBaseSet.has(e.from) && ccBaseSet.has(e.to),
          ),
          ports: baseCoarsePorts.filter((p) => ccBaseSet.has(p.nodeId)),
          connectedSet: baseWorld.connectedSet,
          siblingSet: baseWorld.siblingSet,
          lr: 0,
          stepCap: 0,
          sand: false,
          collect: collected,
          // Cohesion mirror: the pre-edit world's frame pull, under the
          // PRE-EDIT assignment, at the pre-edit positions — unmirrored it
          // reads as a phantom edit response through the trim anchors.
          ...(input.editBaseline?.partition
            ? {
                frameGroupOf: coarseGroupOf(
                  input.editBaseline.partition,
                  ccBase,
                  (id) => oldSuperByKey.get(id),
                ),
                frameK: W_FRAME_COHESION,
                ...(input.editBaseline.partitionParents
                  ? { frameParents: input.editBaseline.partitionParents }
                  : {}),
              }
            : {}),
        });
        // Transfer anchors onto CURRENT coarse nodes. Rules:
        //  - ordinary node with an individual baseline body → its g;
        //  - current super whose baseline assembly has the SAME members →
        //    the baseline super's g (same body, same template);
        //  - re-formed super (membership changed / newly born) → hold at
        //    its hub-derived pose with g = 0: the members snapping to
        //    template slots IS the response, but the BODY must stay
        //    moored — a single flipped wing must not send an assembly of
        //    untouched nodes drifting through unanchored iterations;
        //  - ex-member gone organic (its baseline forces lived inside a
        //    super) → hold at the rendered spot (g = 0), deltas act
        //    through the K-lean.
        for (const id of cc.ids) {
          if (pinned.has(id) || !previous.has(id)) continue;
          if (editFree?.has(id)) continue; // decisive movers stay unanchored
          const at = trimBase.get(id);
          if (!at) continue; // no pre-edit state — free (e.g. spawned)
          const curAsm = superOf.get(id);
          let g: { x: number; y: number } | undefined;
          if (curAsm) {
            const oldAsm = oldSuperByKey.get(curAsm.superId);
            g =
              oldAsm && sameMembers(oldAsm.memberIds, curAsm.memberIds)
                ? collected.get(id)
                : { x: 0, y: 0 };
          } else {
            g = collected.get(id);
            if (!g && oldMemberToSuper.has(id)) g = { x: 0, y: 0 };
          }
          if (!g) continue;
          formationTargets.set(id, {
            x: at.x + g.x / W_TRIM,
            y: at.y + g.y / W_TRIM,
          });
        }
      }
    } else {
      // Calibration state: the gesture baseline when the app supplies one,
      // else the current warm seed. Positions are swapped in temporarily,
      // the gradient collected, then restored — pure measurement.
      const baseline = trimBase.size > 0 ? trimBase : undefined;
      const saved = new Map<string, { x: number; y: number }>();
      if (baseline) {
        for (const [id, r] of baseline) {
          const p = pos.get(id);
          if (!p) continue;
          saved.set(id, { x: p.x, y: p.y });
          p.x = r.x;
          p.y = r.y;
        }
      }
      for (const cc of solverCCs) {
        const collected = new Map<string, { x: number; y: number }>();
        stepGradient({
          nodeIds: cc.ids,
          pos,
          size,
          pinned,
          edges: cc.edges,
          directedEdges: biasEdgesOf(cc.directedEdges),
          ports: cc.ports,
          connectedSet,
          siblingSet,
          lr: 0,
          stepCap: 0,
          sand: false,
          collect: collected,
          // Cohesion mirror — same gate as every live phase (incl. drag),
          // so the measured profile equals what the phases apply.
          ...(frameGroupOfCoarse
            ? {
                frameGroupOf: frameGroupOfCoarse,
                frameK: W_FRAME_COHESION,
                ...(forcedParents ? { frameParents: forcedParents } : {}),
              }
            : {}),
        });
        for (const [id, g] of collected) {
          if (pinned.has(id)) continue;
          const at = baseline?.get(id) ?? pos.get(id);
          if (!at || !previous.has(id)) continue; // cold nodes stay free
          formationTargets.set(id, {
            x: at.x + g.x / W_TRIM,
            y: at.y + g.y / W_TRIM,
          });
        }
      }
      if (baseline) {
        for (const [id, sPos] of saved) {
          const p = pos.get(id)!;
          p.x = sPos.x;
          p.y = sPos.y;
        }
      }
    }
  }


  // A sand+incremental solve is a MAINTENANCE solve: placeSpace picks that
  // combination only for a same-topology recompute (zero cold nodes), whose
  // job is to KEEP the standing layout — not to re-litigate spring physics
  // against the crystallized/density-padded geometry (that fight is what
  // slid warm layouts ~90px). Skip the float rounds and the tidy rest;
  // organize re-crystallizes deterministically and separation guarantees
  // gaps below. (`maintenance` is declared next to `sand` above.)

  for (let round = 0; round < (maintenance ? 0 : rounds); round++) {
    for (let iter = 0; iter < iterPerRound; iter++) {
      // Schedule lerps across the whole run (rounds × iters), not each
      // round, so the last rounds finish on small careful steps instead of
      // bouncing around with the full starting learning rate every time.
      const t =
        (round * iterPerRound + iter) /
        Math.max(1, rounds * iterPerRound - 1);
      const lr = isDrag ? LR_DRAG : lerp(LR_START, LR_END, t);
      const stepCap = isDrag
        ? STEP_CAP_DRAG
        : lerp(STEP_CAP_START, STEP_CAP_END, t);

      // Sub-stepping: one "iter" internally runs N small gradient
      // steps, each with LR/N and step_cap/N, recomputing forces
      // between them. This is equivalent to using a finer time delta
      // on stiff forces (close-range repulsion) — per sub-step moves
      // stay small enough to avoid explicit-Euler overshoot, while the
      // total motion per iter is capped by the usual step_cap. Far
      // from the stiff region the behaviour converges to the
      // un-sub-stepped version.
      const N_SUBSTEPS = 5;
      let moved = 0;
      for (const cc of solverCCs) {
        for (let sub = 0; sub < N_SUBSTEPS; sub++) {
          moved += stepGradient({
            nodeIds: cc.ids,
            pos,
            size,
            pinned,
            edges: cc.edges,
            directedEdges: biasEdgesOf(cc.directedEdges),
            ports: cc.ports,
            connectedSet,
            siblingSet,
            lr: lr / N_SUBSTEPS,
            stepCap: stepCap / N_SUBSTEPS,
            sand,
            ...(budget !== "full" && formationTargets.size > 0
              ? { anchors: formationTargets, anchorK: W_TRIM }
              : {}),
            ...(sand && !isDrag ? { sandStatic: SAND_F_STATIC_HOLD } : {}),
            // Frame cohesion — a standing force for forced groups, so it
            // must act in EVERY phase the trim anchors act in (rounds, rest,
            // drag): the calibration collect includes it, and any phase that
            // omits it turns the anchor compensation into a phantom push
            // (measured: the nested no-op pin drifted 3.7px through the
            // anchored REST pass when rest omitted cohesion).
            ...(frameGroupOfCoarse
              ? {
                  frameGroupOf: frameGroupOfCoarse,
                  frameK: W_FRAME_COHESION,
                  ...(forcedParents ? { frameParents: forcedParents } : {}),
                }
              : {}),
          });
        }
      }

      // No rectangle non-overlap projection — the strong short-range
      // repulsion tier (W_REPULSE_STRONG / d_rect²) handles keeping
      // nodes from overlapping purely as a force, which is smooth and
      // differentiable where the projection was a hard, piecewise jump.

      // Yield after each iteration — animation shows one gradient step
      // per frame; sync solver doesn't await and moves on.
      yield { kind: "iter", round, result: packResult(nodeIds, pos, size, dir, rawPinned, assemblies) };

      if (iter > 8 && moved < CONVERGE_EPS * Math.max(1, solverScope)) break;
    }

    // Structural repair after each round — except the very last. Skipped
    // in sand mode: repair teleports contradict "hold the standing layout"
    // (sand = drag neighbourhoods and same-topology warm re-solves).
    // Per-CC: each CC's stretched edges live entirely within the CC, so
    // placeAtIdeal and ghostRepair both run scoped to one CC at a time.
    if (!isDrag && !sand && round < rounds - 1) {
      for (const cc of solverCCs) {
        const placeGen = placeAtIdealSteps(
          cc.ids,
          pos,
          size,
          cc.edges,
          pinned,
          editFree,
        );
        let placeStep = placeGen.next();
        while (!placeStep.done) {
          yield {
            kind: "placeAtIdeal",
            round,
            result: packResult(nodeIds, pos, size, dir, rawPinned, assemblies),
          };
          placeStep = placeGen.next();
        }
        // Ghost-repair replaces the old teleport-repair pass. For each
        // overstretched edge we pick the less-constrained endpoint, mark
        // it as a "ghost" (transparent to repulsion), and let the gradient
        // pull it through any intervening nodes toward its peer. After a
        // travel phase, we drop ghost and run a few settle iters so strong
        // repulsion bounces it out of overlap into the nearest free spot.
        //
        // Smoother than instant teleport AND completely driven by the
        // existing physics — no separate energy-scoring heuristic.
        const ghostGen = ghostRepairSteps({
          nodeIds: cc.ids,
          pos,
          size,
          edges: cc.edges,
          directedEdges: biasEdgesOf(cc.directedEdges),
          ports: cc.ports,
          connectedSet,
          siblingSet,
          pinned,
          deg,
          scope: editFree,
        });
        let ghostStep = ghostGen.next();
        while (!ghostStep.done) {
          const v = ghostStep.value;
          yield {
            kind: v.phase === "travel" ? "ghost" : "ghostSettle",
            round,
            result: packResult(nodeIds, pos, size, dir, rawPinned, assemblies),
            moverId: v.moverId,
          };
          ghostStep = ghostGen.next();
        }
      }
    }
  }

  // Post-pack: CCs may have grown / shrunk vs the pre-pack estimate. Re-pack
  // so the tidy phase starts with non-overlapping CC bboxes. Sand friction
  // in tidy then holds the packed layout while allowing small adjustments.
  packComponents(ccs, pos, size);

  // Final rest phase — pure sand friction, no extra alignment or snap
  // forces. Same gradient profile the drag-budget solver sees, so the
  // equilibrium reached here is identical to the equilibrium during a
  // subsequent drag → no "jump" when the user first touches a node.
  // Always runs the full TIDY_ITERS — no convergence early-exit, because
  // per-iter motion is intentionally tiny (sand friction holds most
  // nodes) and a naive check would bail before the rest is visible to
  // the animation viewer. Maintenance solves skip it (see above).
  if (!isDrag && !maintenance && nodeIds.length >= 2) {
    for (let iter = 0; iter < TIDY_ITERS; iter++) {
      stepGradient({
        nodeIds,
        pos,
        size,
        pinned,
        edges: edgeForces,
        directedEdges: biasEdgesOf(cleanEdges),
        ports,
        connectedSet,
        siblingSet,
        lr: TIDY_LR,
        stepCap: TIDY_STEP_CAP,
        sand: true,
        ...(budget !== "full" && formationTargets.size > 0
          ? { anchors: formationTargets, anchorK: W_TRIM }
          : {}),
        ...(budget === "incremental" ? { sandStatic: SAND_F_STATIC_HOLD } : {}),
        // Cohesion rides every anchor-bearing phase — see the rounds note.
        ...(frameGroupOfCoarse
          ? {
              frameGroupOf: frameGroupOfCoarse,
              frameK: W_FRAME_COHESION,
              ...(forcedParents ? { frameParents: forcedParents } : {}),
            }
          : {}),
      });
      yield {
        kind: "align",
        round: rounds - 1,
        result: packResult(nodeIds, pos, size, dir, rawPinned, assemblies),
      };
    }
  }

  // Wall-anchor settle — pull every ported node to its space wall. Operates on
  // the final absolute positions (post-pack), so it catches shelf-packed
  // singleton boundary nodes the per-CC solver never touched. Interior
  // (non-ported) nodes freeze as obstacles; ported nodes spring toward a wall
  // column and repel each other along it, so they spread instead of stacking.
  //
  // Runs on every non-drag recompute (cold full AND warm incremental edits) so
  // a node that gains/loses an external connection re-anchors to its wall right
  // away. Skipped only in sand mode — i.e. during a drag, and any "hold
  // position" relax — so the user's in-progress placement isn't yanked. Hand-
  // pinned nodes are exempt regardless (filtered out of `wallTarget` below).
  // Gated on the RESOLVED sand flag (not raw input.mode): sand never applies
  // to full budgets, so a full+sand input still wall-anchors as promised.
  if (!sand && ports.length > 0 && nodeIds.length >= 2) {
    // Net direction per non-pinned ported node: +1 leans out (right wall),
    // −1 leans in (left wall); a node with balanced in+out ports nets 0 and is
    // left to the ordinary forces. An `inout` port is that balance already
    // folded into one port, so it contributes 0 — which is what the in+out PAIR
    // it replaces summed to, keeping the solve byte-identical across the merge.
    // Net wall direction for EVERY ported node — also the interior-bbox
    // exclusion set (a held wall node counted as "interior" would push the
    // derived wall column one layer past the standing wall).
    const portedNet = new Map<string, number>();
    for (const p of ports) {
      if (pinned.has(p.nodeId) || !idSet.has(p.nodeId)) continue;
      const lean = p.direction === "inout" ? 0 : p.direction === "out" ? 1 : -1;
      portedNet.set(p.nodeId, (portedNet.get(p.nodeId) ?? 0) + lean);
    }
    // Edit solves re-anchor only nodes whose ports the edit changed —
    // everything else is held by its trim anchor and must not be yanked
    // to a re-derived wall column.
    const portedDir = new Map<string, number>();
    for (const [id, net] of portedNet) {
      if (editWallScope && !editWallScope.has(id)) continue;
      portedDir.set(id, net);
    }

    // Interior bbox = nodes with no port. When every node is a boundary node,
    // fall back to the whole-space bbox so the walls still have a reference.
    let minX = Infinity, maxX = -Infinity, sawInterior = false;
    for (const id of nodeIds) {
      if (portedNet.has(id)) continue;
      const p = pos.get(id)!; const s = size.get(id)!;
      minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x + s.w); sawInterior = true;
    }
    if (!sawInterior) {
      for (const id of nodeIds) {
        const p = pos.get(id)!; const s = size.get(id)!;
        minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x + s.w);
      }
    }

    let outWallX = maxX + WALL_MARGIN;
    let inWallX = minX - WALL_MARGIN;
    // Edit solves: the STANDING co-wall peers define the column. A wall
    // re-derived from the post-edit interior can sit a whole layer away
    // from where the held wall nodes actually stand (the edited node may
    // itself have been part of the interior the old wall was derived
    // from), stranding the newly-ported node short of its peers.
    if (editWallScope) {
      let heldOut = -Infinity;
      let heldIn = Infinity;
      for (const [id, net] of portedNet) {
        if (editWallScope.has(id) || net === 0) continue;
        const p = pos.get(id)!; const s = size.get(id)!;
        const cx = p.x + s.w / 2;
        if (net > 0) heldOut = Math.max(heldOut, cx);
        else heldIn = Math.min(heldIn, cx);
      }
      if (heldOut > -Infinity) outWallX = heldOut;
      if (heldIn < Infinity) inWallX = heldIn;
    }
    const wallTarget = new Map<string, number>();
    for (const [id, dir] of portedDir) {
      if (dir === 0) continue;
      wallTarget.set(id, dir > 0 ? outWallX : inWallX);
    }
    // Edit solves: aim each mover at the nearest FREE band along its wall.
    // The held co-wall peers are trim-anchored (a standing repulsion of
    // ~56 force sustains only a ~4px K-lean), so they cannot make room —
    // without a vertical aim the newcomer lands on a peer and gets
    // expelled back into the interior by the settle repulsion.
    const wallTargetY = new Map<string, number>();
    if (editWallScope) {
      for (const [id, dir] of portedDir) {
        if (dir === 0) continue;
        const bands: Array<[number, number]> = [];
        for (const [peer, net] of portedNet) {
          if (peer === id || editWallScope.has(peer)) continue;
          if (Math.sign(net) !== Math.sign(dir)) continue;
          const p = pos.get(peer)!; const sz = size.get(peer)!;
          bands.push([p.y, p.y + sz.h]);
        }
        if (bands.length === 0) continue; // no peers — x-only, as before
        bands.sort((a, b) => a[0] - b[0]);
        const mh = size.get(id)!.h;
        const cur = pos.get(id)!.y + mh / 2;
        // Candidate centre-ys: above the stack, below it, and inside any
        // gap wide enough for the mover + separation gaps.
        const cands: number[] = [];
        cands.push(bands[0]![0] - SEP_GAP - mh / 2);
        cands.push(bands[bands.length - 1]![1] + SEP_GAP + mh / 2);
        for (let b = 0; b + 1 < bands.length; b++) {
          const gapTop = bands[b]![1];
          const gapBottom = bands[b + 1]![0];
          if (gapBottom - gapTop >= mh + 2 * SEP_GAP) {
            cands.push(gapTop + SEP_GAP + mh / 2);
          }
        }
        // Nearest to the current y; ties break to the smaller cy.
        let best = cands[0]!;
        for (const c of cands) {
          const d = Math.abs(c - cur);
          const bd = Math.abs(best - cur);
          if (d < bd || (d === bd && c < best)) best = c;
        }
        wallTargetY.set(id, best);
      }
    }

    if (wallTarget.size > 0) {
      // Freeze interior + already-pinned nodes; only boundary nodes move.
      const settlePinned = new Map(pinned);
      for (const id of nodeIds) {
        if (wallTarget.has(id) || settlePinned.has(id)) continue;
        const p = pos.get(id)!; const s = size.get(id)!;
        settlePinned.set(id, { x: p.x, y: p.y, w: s.w, h: s.h });
      }
      // A ported node often shelf-packs on the OPPOSITE side of the interior
      // from its wall (e.g. an "in" node packed to the right must reach the
      // left wall). Frozen interior repulsion would wall it off mid-cross. So
      // the travel phase makes every ported node a ghost — transparent to
      // repulsion (same mechanism as edge repair) — and the wall spring pulls
      // it straight through to its wall. The settle phase then re-enables
      // repulsion so co-wall nodes spread vertically instead of overlapping.
      const ghostSet = new Set(wallTarget.keys());
      // Edit solves keep the movers ghost for the WHOLE phase: the held
      // co-wall peers are trim-anchored and cannot make room (a standing
      // repulsion of ~56 force sustains only a ~4px K-lean), so a settle
      // sub-phase would just expel the newcomer back into the interior.
      // Instead it lands ON the column and the separation pass — the last
      // moving phase, with its nearest-free-spot guarantee — slots it in
      // along the wall.
      const travelIters = editWallScope
        ? WALL_ANCHOR_ITERS
        : Math.floor(WALL_ANCHOR_ITERS * 0.8);
      for (let iter = 0; iter < WALL_ANCHOR_ITERS; iter++) {
        stepGradient({
          nodeIds,
          pos,
          size,
          pinned: settlePinned,
          edges: edgeForces,
          directedEdges: biasEdgesOf(cleanEdges),
          ports: [], // replaced by the wall spring below
          connectedSet,
          siblingSet,
          lr: WALL_ANCHOR_LR,
          stepCap: WALL_ANCHOR_STEP_CAP,
          sand: false,
          wallTarget,
          wallK: W_WALL_ANCHOR,
          ...(wallTargetY.size > 0 ? { wallTargetY } : {}),
          ...(iter < travelIters ? { ghostSet } : {}),
        });
        yield {
          kind: "align",
          round: rounds - 1,
          result: packResult(nodeIds, pos, size, dir, rawPinned, assemblies),
        };
      }
    }
  }

  // Organize: DECIDE the formation (columns, stacks, acceptance — on a
  // trial copy), then enforce it through standing anchor FORCES and settle
  // the whole system to convergence. The final state is thereby a genuine
  // equilibrium of the live forces — grabbing a node without moving it
  // stirs nothing, and a drag pulls against the formation instead of
  // waking a different equilibrium. Maintenance solves skip (hold).
  // Edit solves skip too: the standing formation is already held by the
  // trim anchors, and re-deriving it from the perturbed state would snap
  // warm nodes to fresh columns — a global answer to a local gesture.
  if (!isDrag && !maintenance && !editSolve && nodeIds.length >= 2) {
    formationTargets.clear();
    for (const cc of solverCCs) {
      const targets = organizeDecide({
        ids: cc.ids,
        pos,
        size,
        pinned,
        edges: cc.edges,
        ...(hoodOf ? { hoodOf } : {}),
      });
      if (targets) {
        for (const [id, t] of targets) formationTargets.set(id, t);
      }
    }
    // Settle to convergence under base forces + formation anchors. No
    // sand: convergence must be real (residual forces ≈ 0), which is what
    // makes the end state drag-stable.
    for (let iter = 0; iter < SETTLE_MAX_ITERS; iter++) {
      const t = Math.min(1, iter / (SETTLE_MAX_ITERS * 0.4));
      const anchorK = W_FORMATION + (W_FORMATION_HOLD - W_FORMATION) * t;
      // Sub-stepped like the main rounds: the strong repulsion tier is
      // stiff, and un-anchored nodes need the finer time delta or they
      // orbit their equilibrium in a permanent explicit-Euler limit cycle.
      const SETTLE_SUBSTEPS = 5;
      let moved = 0;
      // Convergence must measure NET displacement over the iteration:
      // anchored nodes oscillate out-and-back within one gradient+relax
      // cycle, so the raw sub-move sum never drops even at a perfect
      // positional fixed point.
      const iterBefore = new Map<string, { x: number; y: number }>();
      for (const id of nodeIds) {
        const p = pos.get(id)!;
        iterBefore.set(id, { x: p.x, y: p.y });
      }
      // Per-CC, exactly like the rounds and the drag budget — ONE force
      // system everywhere. A global sweep would add cross-CC weak
      // repulsion that no later phase has: packed components slide apart
      // without ever converging, and the "equilibrium" the drag inherits
      // was never reached.
      for (const cc of solverCCs) {
        for (let sub = 0; sub < SETTLE_SUBSTEPS; sub++) {
          moved += stepGradient({
            nodeIds: cc.ids,
            pos,
            size,
            pinned,
            edges: cc.edges,
            directedEdges: biasEdgesOf(cc.directedEdges),
            ports: cc.ports,
            connectedSet,
            siblingSet,
            lr: SETTLE_LR / SETTLE_SUBSTEPS,
            stepCap: SETTLE_STEP_CAP / SETTLE_SUBSTEPS,
            sand: false,
            ...(formationTargets.size > 0
              ? { anchors: formationTargets, anchorK }
              : {}),
          });
        }
      }
      yield {
        kind: "organize",
        round: rounds - 1,
        result: packResult(nodeIds, pos, size, dir, rawPinned, assemblies),
      };
      let netMoved = 0;
      for (const id of nodeIds) {
        const b = iterBefore.get(id)!;
        const p = pos.get(id)!;
        netMoved += (p.x - b.x) ** 2 + (p.y - b.y) ** 2;
      }
      moved = netMoved;
      if (t >= 1 && moved < SETTLE_CONVERGED_EPS * Math.max(1, nodeIds.length)) {
        if ((globalThis as { __ARCH_SETTLE_DEBUG?: boolean }).__ARCH_SETTLE_DEBUG) {
          // eslint-disable-next-line no-console
          console.log(`[settle] targets=${formationTargets.size} iters=${iter} moved=${moved.toFixed(4)}`);
        }
        break;
      }
      if (iter % 100 === 0 && (globalThis as { __ARCH_SETTLE_DEBUG?: boolean }).__ARCH_SETTLE_DEBUG) {
        const movers: string[] = [];
        for (const id of nodeIds) {
          const b = (globalThis as { __ARCH_SETTLE_PREV?: Map<string, { x: number; y: number }> }).__ARCH_SETTLE_PREV?.get(id);
          const p = pos.get(id)!;
          if (b) {
            const d = Math.hypot(p.x - b.x, p.y - b.y);
            if (d > 0.5) movers.push(`${id}:${d.toFixed(1)}`);
          }
        }
        // eslint-disable-next-line no-console
        console.log(`[settle] iter=${iter} moved=${moved.toFixed(3)} movers: ${movers.slice(0, 8).join(" ")}`);
        const snap = new Map<string, { x: number; y: number }>();
        for (const id of nodeIds) {
          const p = pos.get(id)!;
          snap.set(id, { x: p.x, y: p.y });
        }
        (globalThis as { __ARCH_SETTLE_PREV?: Map<string, { x: number; y: number }> }).__ARCH_SETTLE_PREV = snap;
      }
      if (iter === SETTLE_MAX_ITERS - 1 && (globalThis as { __ARCH_SETTLE_DEBUG?: boolean }).__ARCH_SETTLE_DEBUG) {
        // eslint-disable-next-line no-console
        console.log(`[settle] targets=${formationTargets.size} CAP-LIMITED moved=${moved.toFixed(4)}`);
      }
    }
  }

  // P3 — port-corridor stagger: after organize has crystallized the columns,
  // nudge flow-chained ported nodes apart in Y so none sits in an upstream
  // ported node's wall lane (the non-motif "line over the node"; motif hubs are
  // handled by the template). Cold/full only — same gate as organize, so the
  // warm/maintenance path HOLDS the staggered result instead of re-deriving it.
  // Runs BEFORE terminal separation, which then settles any residual overlap.
  // ALL directions (task V): the pass staggers in flow-Y = the pill-lane axis,
  // and pills dock on flow walls (out → flow-downstream), so flow-Y is the right
  // cross axis for every direction — LR/RL (flow-Y = screen-Y) and TB/BT (flow-Y
  // = screen-X) alike. `ports.length > 0` keeps non-ported layouts untouched.
  if (
    !isDrag && !maintenance && !editSolve &&
    ports.length > 0 && nodeIds.length >= 2
  ) {
    clearPortCorridors(nodeIds, pos, size, ports, pinned);
  }

  // Hard overlap removal — the LAST phase that moves nodes, so nothing can
  // re-introduce an overlap after it (the wall-anchor phase above moves
  // ported nodes, which is why separation runs after it, not before).
  // Skipped during drags: transient squeezes while the pointer moves are
  // fine and resolve on release, when the next non-drag solve runs.
  //
  // Frame groups (forced partition) interleave TWO integrity passes until
  // both are simultaneously clean: frame separation (rigid group-fragment
  // translation + stranger eviction, per CC — one fragment per group × CC)
  // and the node-level pass. Either alone undoes the other's work — an
  // evicted stranger can land on a node, and the node pass can shove a
  // stranger back inside a frame rect — so the loop runs frame → node
  // cycles until a frame pass moves nothing. Both are terminal position
  // projections: identical inputs → identical bboxes → drift-safe. The
  // no-partition path below is byte-identical to the classic single pass.
  if (!isDrag && nodeIds.length >= 2) {
    const sortedAll = [...nodeIds].sort();
    const sepArgs = {
      ids: sortedAll,
      pos,
      size,
      pinned,
      gap: SEP_GAP,
      maxPasses: SEP_MAX_PASSES,
      padOf: sepPadOf,
    };
    const runNodePass = function* (
      this: void,
    ): Generator<StressStep, boolean, void> {
      const sepGen = separationPasses(sepArgs);
      let sepStep = sepGen.next();
      while (!sepStep.done) {
        yield {
          kind: "separate",
          round: rounds - 1,
          result: packResult(nodeIds, pos, size, dir, rawPinned, assemblies),
        };
        sepStep = sepGen.next();
      }
      return sepStep.value;
    };
    if (frameGroupOfCoarse && frameGroupOfCoarse.size > 0) {
      const FRAME_NODE_CYCLES = 12;
      let nodeClean = false;
      for (let cycle = 0; cycle < FRAME_NODE_CYCLES; cycle++) {
        let frameMoved = false;
        for (const cc of ccs) {
          if (cc.ids.length < 2) continue;
          const frameGen = frameSeparationPasses({
            ids: [...cc.ids].sort(),
            pos,
            size,
            pinned,
            frameOf: frameGroupOfCoarse,
            ...(forcedParents ? { parents: forcedParents } : {}),
            pad: FRAME_PAD,
            labelH: FRAME_LABEL_H,
            labelSide: frameLabelSide,
            maxPasses: FRAME_SEP_MAX_PASSES,
          });
          let frameStep = frameGen.next();
          while (!frameStep.done) {
            frameMoved = true;
            yield {
              kind: "separate",
              round: rounds - 1,
              result: packResult(nodeIds, pos, size, dir, rawPinned, assemblies),
            };
            frameStep = frameGen.next();
          }
        }
        // Fixed point: the frame pass verified clean over the state the
        // node pass left (nodeClean starts false, so cycle 0 never breaks).
        if (!frameMoved && nodeClean) break;
        nodeClean = yield* runNodePass();
      }
      if (!nodeClean) {
        resolveResidualOverlaps(sepArgs);
        yield {
          kind: "separate",
          round: rounds - 1,
          result: packResult(nodeIds, pos, size, dir, rawPinned, assemblies),
        };
      }
      // Terminal frame guarantee: corridor-squeezed strangers orbit the
      // frame/node passes forever (node pads push them into a frame's pad
      // zone, eviction pushes back — a periodic orbit no cycle budget
      // escapes). Relocate the residuals to a spot clear of frames AND
      // collision-free vs every node, so neither pass reacts again.
      let relocated = false;
      for (const cc of ccs) {
        if (cc.ids.length < 2) continue;
        if (
          resolveFrameStrangerResiduals({
            ids: [...cc.ids].sort(),
            pos,
            size,
            pinned,
            frameOf: frameGroupOfCoarse,
            ...(forcedParents ? { parents: forcedParents } : {}),
            pad: FRAME_PAD,
            labelH: FRAME_LABEL_H,
            labelSide: frameLabelSide,
            maxPasses: FRAME_SEP_MAX_PASSES,
            gap: SEP_GAP,
            padOf: sepPadOf,
          }).length > 0
        ) {
          relocated = true;
        }
      }
      if (relocated) {
        yield {
          kind: "separate",
          round: rounds - 1,
          result: packResult(nodeIds, pos, size, dir, rawPinned, assemblies),
        };
      }
    } else {
      const clean = yield* runNodePass();
      if (!clean) {
        // Residuals (pin squeezes, flapping clusters) — re-place onto the
        // nearest free spots. Guaranteed clean afterwards.
        resolveResidualOverlaps(sepArgs);
        yield {
          kind: "separate",
          round: rounds - 1,
          result: packResult(nodeIds, pos, size, dir, rawPinned, assemblies),
        };
      }
    }
  }

  const packed = packResult(nodeIds, pos, size, dir, rawPinned, assemblies);
  if (!hoodWorld) return packed;
  // RAW node id → CC index: the exact decomposition this solve shelf-packed
  // (assembly members inherit their body's component). Frame rects must
  // split on THIS, not a re-derivation — findComponents unions port peers.
  const components = new Map<string, number>();
  ccs.forEach((cc, i) => {
    for (const id of cc.ids) {
      const a = superOf.get(id);
      if (a) for (const m of a.memberIds) components.set(m, i);
      else components.set(id, i);
    }
  });
  return { ...packed, neighborhoods: hoodWorld.rawHoodOf, components };
}

/** Pack positions + bbox into a StressResult, mapping flow space back to
 *  screen space. Positions stay in whatever coordinate frame the solver
 *  settled on — do NOT re-origin here. `buildSpace` wraps whatever frame we
 *  return with its own originX/width computation. */
function packResult(
  nodeIds: readonly string[],
  pos: ReadonlyMap<string, { x: number; y: number }>,
  size: ReadonlyMap<string, { w: number; h: number }>,
  dir: FlowDirection,
  rawPinned?: ReadonlyMap<string, Rect>,
  assemblies?: readonly MotifAssembly[],
): StressResult {
  const positions = new Map<string, Rect>();
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  const push = (id: string, r: Rect) => {
    positions.set(id, r);
    minX = Math.min(minX, r.x);
    minY = Math.min(minY, r.y);
    maxX = Math.max(maxX, r.x + r.w);
    maxY = Math.max(maxY, r.y + r.h);
  };
  const superIds =
    assemblies && assemblies.length > 0
      ? new Set(assemblies.map((a) => a.superId))
      : undefined;
  for (const id of nodeIds) {
    if (superIds?.has(id)) continue; // expanded to members below
    const p = pos.get(id)!;
    const s = size.get(id)!;
    // Pinned rects pass through VERBATIM (screen space). The RL/BT mirror
    // is `-(x+w)`, whose addition rounds — a pin would come back 1e-13 off,
    // which is enough to break exact-compare fast paths (drag reuse,
    // React identity) and violates "pins never move".
    const r = rawPinned?.get(id) ?? fromFlowRect({ x: p.x, y: p.y, w: s.w, h: s.h }, dir);
    push(id, r);
  }
  // Expand assemblies: members at their template slots inside the solved
  // super-node frame. The super-node itself never reaches the output.
  for (const a of assemblies ?? []) {
    const p = pos.get(a.superId);
    if (!p) continue;
    for (const [memberId, o] of a.offsets) {
      const s = size.get(memberId)!;
      // A pinned hub passes through VERBATIM (same 1ulp-mirror reasoning
      // as the main loop) — its assembly is positioned so the slots agree.
      push(
        memberId,
        rawPinned?.get(memberId) ??
          fromFlowRect({ x: p.x + o.dx, y: p.y + o.dy, w: s.w, h: s.h }, dir),
      );
    }
  }
  const width = maxX - minX + 2 * PADDING;
  const height = maxY - minY + 2 * PADDING;
  return { positions, width, height };
}

// ---- Force world -------------------------------------------------------------

/**
 * Derive the position-independent force system of a graph: undirected
 * degrees (hub damping), the density census ("the more arrows, the more
 * space" — mesh-ness plus arrow load, baked into per-edge ideal lengths so
 * every phase shares one measuring stick), per-edge spring stiffness/ideal,
 * and the connected/sibling pair sets that gate weak repulsion.
 *
 * Called once for the CURRENT graph on every solve, and once more for the
 * PRE-EDIT graph when calibrating a model-edit gesture — both worlds are
 * measured with identical rules, so the difference between them is exactly
 * the edit's own force delta.
 */
function buildForceWorld(
  nodeIds: readonly string[],
  cleanEdges: readonly CoarseEdge[],
  teamOf: ReadonlyMap<string, string | undefined>,
): {
  deg: Map<string, number>;
  edgeForces: EdgeForce[];
  connectedSet: Set<string>;
  siblingSet: Set<string>;
  crowdOf: Map<string, number>;
} {
  // Degree for hub damping (undirected).
  const deg = new Map<string, number>();
  for (const id of nodeIds) deg.set(id, 0);
  for (const e of cleanEdges) {
    deg.set(e.from, (deg.get(e.from) ?? 0) + 1);
    deg.set(e.to, (deg.get(e.to) ?? 0) + 1);
  }

  // Density census — crowd = mesh-ness (edges among a node's neighbors),
  // position-independent.
  const adjacency = new Map<string, Set<string>>();
  for (const id of nodeIds) adjacency.set(id, new Set());
  for (const e of cleanEdges) {
    adjacency.get(e.from)?.add(e.to);
    adjacency.get(e.to)?.add(e.from);
  }
  const crowdOf = new Map<string, number>();
  for (const id of nodeIds) {
    // Mesh-ness: edges among this node's neighbors — arrows that compete
    // for the same area. Chains/fans/gateways score ~0.
    const nbs = [...adjacency.get(id)!];
    let m = 0;
    for (let i = 0; i < nbs.length; i++) {
      for (let j = i + 1; j < nbs.length; j++) {
        if (adjacency.get(nbs[i]!)!.has(nbs[j]!)) m++;
      }
    }
    // Hubs are dense in arrows: the node's own arrow load counts too.
    m += Math.max(0, nbs.length - CROWD_DEG_BASE);
    crowdOf.set(id, m);
  }
  const densityPadOf = (a: string, b: string): number => {
    // MAX, not mean: a crowded hub paired with a calm leaf is exactly the
    // edge that needs the pad (11 partners at leaf-tier ideals = swarm),
    // and averaging diluted the hub's crowd to zero pad on those edges.
    const worst = Math.max(crowdOf.get(a) ?? 0, crowdOf.get(b) ?? 0);
    return Math.min(
      DENSITY_IDEAL_MAX,
      DENSITY_IDEAL_PER_CROWD * Math.max(0, worst - CROWD_BASE),
    );
  };

  // Per-edge stiffness (with hub damping) and ideal distance (with degree-
  // dependent spread, same-team pull, and density stretch).
  const edgeForces: EdgeForce[] = cleanEdges.map((e) => {
    const dFrom = deg.get(e.from) ?? 1;
    const dTo = deg.get(e.to) ?? 1;
    const stiffness = W_CONN / Math.sqrt(Math.max(1, dFrom) * Math.max(1, dTo));
    const sharedLoad = Math.min(dFrom, dTo);
    const tFrom = teamOf.get(e.from);
    const tTo = teamOf.get(e.to);
    const sameTeam = tFrom !== undefined && tFrom === tTo;
    const tableIdx = Math.min(sharedLoad, IDEAL_BY_MIN_DEGREE.length - 1);
    let idealLen = IDEAL_BY_MIN_DEGREE[tableIdx] ?? IDEAL_CAP;
    if (sameTeam) idealLen = Math.max(0, idealLen - IDEAL_TEAM_BONUS);
    idealLen += densityPadOf(e.from, e.to);
    return {
      from: e.from,
      to: e.to,
      stiffness,
      idealLen,
      ...(e.origFrom !== undefined ? { origFrom: e.origFrom } : {}),
      ...(e.origTo !== undefined ? { origTo: e.origTo } : {}),
    };
  });

  // Connected-pair lookup — pairs in here skip the weak (medium-range)
  // repulsion (their spring controls the distance). Edges live entirely
  // within a single CC (by definition of weakly-connected), so the global
  // set restricted to within-CC pairs is equivalent to per-CC sets.
  const connectedSet = new Set<string>();
  for (const e of cleanEdges) {
    connectedSet.add(`${e.from}|${e.to}`);
    connectedSet.add(`${e.to}|${e.from}`);
  }

  // Sibling-pair lookup — two nodes are siblings if they share at least one
  // connected peer (e.g. two leaves of the same hub). Siblings also skip
  // weak repulsion so a hub's children can cluster tight around it without
  // medium-range push holding them apart.
  const peersOf = new Map<string, Set<string>>();
  for (const id of nodeIds) peersOf.set(id, new Set());
  for (const e of cleanEdges) {
    peersOf.get(e.from)!.add(e.to);
    peersOf.get(e.to)!.add(e.from);
  }
  const siblingSet = new Set<string>();
  for (const [, peers] of peersOf) {
    const arr = [...peers];
    for (let i = 0; i < arr.length; i++) {
      for (let j = i + 1; j < arr.length; j++) {
        const a = arr[i]!;
        const b = arr[j]!;
        siblingSet.add(`${a}|${b}`);
        siblingSet.add(`${b}|${a}`);
      }
    }
  }
  return { deg, edgeForces, connectedSet, siblingSet, crowdOf };
}

/** Browser-only dogfood flag read. Node 22+ ships its own
 *  `globalThis.localStorage`, and touching ANY member of it without
 *  `--localstorage-file` both throws and prints a runtime warning to the
 *  user's stderr — so headless (`archlang render`, workers, tests) must be
 *  ruled out by the `document` check BEFORE the store is touched at all,
 *  not by catching the throw. Fails closed off the browser. */
function readDogfoodFlag(key: string): string | null {
  try {
    const g = globalThis as { document?: unknown; localStorage?: Storage };
    if (typeof g.document === "undefined") return null;
    return g.localStorage?.getItem(key) ?? null;
  } catch {
    return null; // sandboxed frames throw on localStorage access
  }
}

/** Dogfood switch for the neighborhood solve (P2, pre-flip). Read per
 *  solve so a console flip applies on the next re-solve; ~µs, and the
 *  partition behind it is memoized anyway. */
function readNeighborhoodsSwitch(): boolean {
  return readDogfoodFlag("arch.layout.neighborhoods") === "1";
}

/**
 * Neighborhood maps for one (raw graph, coarse graph) pair. The partition
 * runs on the RAW topology (R1) and projects onto coarse ids through
 * assembly membership: a formation is a partition seed, so all its
 * members share one hood and any member represents the body (gateways
 * use the hub, shelves the first member). `hoodPairExempt` holds the
 * directed cross-hood pairs inside a level-2 SCC of the hood graph —
 * exempt from flow order like node-level SCC edges. Called once for the
 * live world and once for the edit-calibration baseline, so both worlds
 * classify edges with identical rules.
 */
/** Hood id of a forced group — reserved `~group~` scheme so a forced hood
 *  can never collide with a topology hood (`~hood~<member>`). */
const groupHoodId = (groupId: string): string => `~group~${groupId}`;

/**
 * Raw node id → hood id under an optional FORCED partition: mapped nodes go
 * to their group's hood verbatim; the remainder (ids, edges among them,
 * sizes, hysteresis reference) gets the normal topology partition. With no
 * `forced` this is exactly the classic partition call.
 */
function buildRawHood(args: {
  rawIds: readonly string[];
  rawEdges: readonly StressEdge[];
  sizes: ReadonlyMap<string, { w: number; h: number }>;
  previous?: ReadonlyMap<string, string>;
  forced?: ReadonlyMap<string, string>;
}): Map<string, string> {
  const rawHood = new Map<string, string>();
  let ids = args.rawIds;
  let edges = args.rawEdges;
  let previous = args.previous;
  if (args.forced && args.forced.size > 0) {
    const forced = args.forced;
    for (const id of args.rawIds) {
      const g = forced.get(id);
      if (g !== undefined) rawHood.set(id, groupHoodId(g));
    }
    ids = args.rawIds.filter((id) => !rawHood.has(id));
    edges = args.rawEdges.filter(
      (e) => !rawHood.has(e.from) && !rawHood.has(e.to),
    );
    if (previous) {
      const rest = new Map<string, string>();
      for (const [id, h] of previous) if (!rawHood.has(id)) rest.set(id, h);
      previous = rest;
    }
    if (ids.length === 0) return rawHood;
  }
  for (const h of partitionNeighborhoods(ids, edges, args.sizes, previous)) {
    for (const m of h.memberIds) rawHood.set(m, h.id);
  }
  return rawHood;
}

function buildHoodMaps(args: {
  coarseIds: readonly string[];
  rawIds: readonly string[];
  rawEdges: readonly StressEdge[];
  sizes: ReadonlyMap<string, { w: number; h: number }>;
  coarseEdges: readonly CoarseEdge[];
  assemblyOf: (id: string) => MotifAssembly | undefined;
  /** R6 hysteresis reference: raw node id → previous hood id. */
  previous?: ReadonlyMap<string, string>;
  /** Forced partition (frame groups): raw node id → group id. */
  forced?: ReadonlyMap<string, string>;
}): {
  hoodOf: Map<string, string>;
  hoodPairExempt: Set<string>;
  rawHoodOf: Map<string, string>;
} {
  const rawHood = buildRawHood({
    rawIds: args.rawIds,
    rawEdges: args.rawEdges,
    sizes: args.sizes,
    ...(args.previous ? { previous: args.previous } : {}),
    ...(args.forced ? { forced: args.forced } : {}),
  });
  const hoodOf = new Map<string, string>();
  for (const id of args.coarseIds) {
    const a = args.assemblyOf(id);
    const rep = a
      ? a.kind === "gateway"
        ? a.hubId
        : [...a.memberIds].sort()[0]!
      : id;
    const h = rawHood.get(rep);
    if (h !== undefined) hoodOf.set(id, h);
  }
  const hoodIds = [...new Set(hoodOf.values())].sort();
  const seenHoodEdge = new Set<string>();
  const hoodEdges: { from: string; to: string }[] = [];
  for (const e of args.coarseEdges) {
    const ha = hoodOf.get(e.from);
    const hb = hoodOf.get(e.to);
    if (ha === undefined || hb === undefined || ha === hb) continue;
    const k = `${ha}|${hb}`;
    if (seenHoodEdge.has(k)) continue;
    seenHoodEdge.add(k);
    hoodEdges.push({ from: ha, to: hb });
  }
  const comp = sccComponents(hoodIds, hoodEdges);
  const hoodPairExempt = new Set<string>();
  for (const e of hoodEdges) {
    if (comp.get(e.from) !== undefined && comp.get(e.from) === comp.get(e.to)) {
      hoodPairExempt.add(`${e.from}|${e.to}`);
    }
  }
  return { hoodOf, hoodPairExempt, rawHoodOf: rawHood };
}

/**
 * Per-edge ideal silhouette gaps — EXACTLY the values the connection
 * springs use (degree tiers + same-team bonus + density pads), keyed
 * `from|to`. Exposed for the contract metrics (C1 stretch): the harness
 * must judge edges by the same ideals the solver aims for, or the gate
 * drifts away from the physics it is supposed to check.
 */
export function edgeIdealGaps(
  nodes: readonly StressNode[],
  edges: readonly StressEdge[],
): Map<string, number> {
  const teamOf = new Map<string, string | undefined>(
    nodes.map((n) => [n.id, n.team]),
  );
  const clean = edges.filter(
    (e) => e.from !== e.to && teamOf.has(e.from) && teamOf.has(e.to),
  );
  const { edgeForces } = buildForceWorld(
    nodes.map((n) => n.id),
    clean,
    teamOf,
  );
  const out = new Map<string, number>();
  for (const f of edgeForces) out.set(`${f.from}|${f.to}`, f.idealLen);
  return out;
}

// ---- Weakly-connected components ------------------------------------------

/**
 * Per-component bundle. Each weakly-connected component (CC) is solved in
 * its own coordinate frame: independent springs, independent flow bias,
 * independent parent-gravity centroid, independent all-pairs repulsion.
 * After the main solver loop, `packComponents` shelf-packs the resulting
 * bounding boxes onto the canvas so CCs don't overlap each other.
 *
 * - `isCold` = no member is pinned and no member has a previous-position
 *   warm start. Only cold CCs are eligible for pack-translation; warm /
 *   pinned CCs keep their absolute coords so user-placed nodes and the
 *   previous frame's layout stay continuous across resolves.
 */
/** A coarse-graph edge that remembers its raw (pre-coarsen) endpoints. */
type CoarseEdge = StressEdge & {
  readonly origFrom?: string;
  readonly origTo?: string;
};

/** Spring endpoint override: anchor the spring at a member SLOT inside a
 *  super-node body (dx/dy = slot centre from the body's top-left) and
 *  measure the silhouette with the member's own size. The gradient still
 *  lands on the body — external forces move the whole formation. */
type SlotAnchor = {
  readonly dx: number;
  readonly dy: number;
  readonly w: number;
  readonly h: number;
};

type EdgeForce = {
  readonly from: string;
  readonly to: string;
  readonly stiffness: number;
  readonly idealLen: number;
  readonly origFrom?: string;
  readonly origTo?: string;
  readonly fromAnchor?: SlotAnchor;
  readonly toAnchor?: SlotAnchor;
};

interface Component {
  readonly ids: string[];
  readonly nodes: StressNode[];
  readonly edges: EdgeForce[];
  readonly directedEdges: StressEdge[];
  readonly ports: StressPort[];
  readonly isCold: boolean;
}

/**
 * Bucket nodes into weakly-connected components via union-find on
 * `cleanEdges`. Singletons (degree-zero nodes) form trivial 1-node CCs.
 * Edge, port, and node lists are pre-filtered per component so the main
 * solver loop can just hand each CC's slice to `stepGradient` without
 * re-scanning the full input each iteration.
 */
function findComponents(
  nodes: readonly StressNode[],
  cleanEdges: readonly StressEdge[],
  edgeForces: readonly EdgeForce[],
  ports: readonly StressPort[],
  pinned: ReadonlyMap<string, Rect>,
  previous: ReadonlyMap<string, Rect>,
): Component[] {
  const parent = new Map<string, string>();
  const find = (x: string): string => {
    let r = x;
    while (parent.get(r)! !== r) r = parent.get(r)!;
    let n = x;
    while (parent.get(n)! !== r) {
      const next = parent.get(n)!;
      parent.set(n, r);
      n = next;
    }
    return r;
  };
  for (const n of nodes) parent.set(n.id, n.id);
  for (const e of cleanEdges) {
    const ra = find(e.from);
    const rb = find(e.to);
    if (ra !== rb) parent.set(ra, rb);
  }

  // Union nodes that share an external-port peer. They have no edge between
  // them in THIS space (their connection lives in another space), but they all
  // flow to the same outside target, so they belong together: one component
  // that the solver clusters + biases to the same wall, instead of N singleton
  // CCs that shelf-pack into an unrelated-looking row. Only real nodes (in the
  // id set) participate; a peer that lives in another space is never a node
  // here, it just groups the local ports that name it.
  const peerGroups = new Map<string, string[]>();
  for (const p of ports) {
    if (p.peerId === undefined || !parent.has(p.nodeId)) continue;
    const arr = peerGroups.get(p.peerId) ?? [];
    arr.push(p.nodeId);
    peerGroups.set(p.peerId, arr);
  }
  for (const members of peerGroups.values()) {
    for (let i = 1; i < members.length; i++) {
      const ra = find(members[0]!);
      const rb = find(members[i]!);
      if (ra !== rb) parent.set(ra, rb);
    }
  }

  const buckets = new Map<string, {
    ids: string[];
    nodes: StressNode[];
    edges: EdgeForce[];
    directedEdges: StressEdge[];
    ports: StressPort[];
    anyAnchored: boolean;
  }>();
  for (const n of nodes) {
    const r = find(n.id);
    let c = buckets.get(r);
    if (!c) {
      c = { ids: [], nodes: [], edges: [], directedEdges: [], ports: [], anyAnchored: false };
      buckets.set(r, c);
    }
    c.ids.push(n.id);
    c.nodes.push(n);
    if (pinned.has(n.id) || previous.has(n.id)) c.anyAnchored = true;
  }
  for (const e of cleanEdges) buckets.get(find(e.from))!.directedEdges.push(e);
  for (const ef of edgeForces) buckets.get(find(ef.from))!.edges.push(ef);
  for (const p of ports) {
    const c = buckets.get(find(p.nodeId));
    if (c) c.ports.push(p);
  }

  // Co-flow springs: give each DIRECT peer-sharing pair a weak spring so the
  // solver clusters them (a real finite target distance) instead of exiling
  // them to buildTargetDistances' large substitute distance — the union
  // grouped them precisely because they belong near each other. Skip a pair a
  // real edge already connects (its spring governs), and dedupe repeated
  // shares of the same/other peers between the same pair.
  const realPairs = new Set<string>();
  for (const ef of edgeForces) realPairs.add(pairKey(ef.from, ef.to));
  const coflowSeen = new Set<string>();
  for (const members of peerGroups.values()) {
    const uniq = [...new Set(members)];
    for (let i = 1; i < uniq.length; i++) {
      const a = uniq[0]!;
      const b = uniq[i]!;
      const key = pairKey(a, b);
      if (realPairs.has(key) || coflowSeen.has(key)) continue;
      coflowSeen.add(key);
      buckets.get(find(a))!.edges.push({
        from: a,
        to: b,
        stiffness: W_CONN * PEER_UNION_STIFFNESS_SCALE,
        idealLen: PEER_UNION_IDEAL,
      });
    }
  }

  // Deterministic CC order: by smallest id within the CC. Keeps successive
  // solves stable (and matches the existing `hash01` ordering used by
  // legacy isolated-row layout for singletons).
  const out = [...buckets.values()].map((b) => ({
    ids: b.ids,
    nodes: b.nodes,
    edges: b.edges,
    directedEdges: b.directedEdges,
    ports: b.ports,
    isCold: !b.anyAnchored,
  }));
  out.sort((a, b) => {
    const ka = minId(a.ids);
    const kb = minId(b.ids);
    return ka < kb ? -1 : ka > kb ? 1 : 0;
  });
  return out;
}

/** Order-independent pair key for de-duping undirected node pairs. */
function pairKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

function minId(ids: readonly string[]): string {
  let m = ids[0]!;
  for (let i = 1; i < ids.length; i++) if (ids[i]! < m) m = ids[i]!;
  return m;
}

/**
 * Shelf-pack components onto the canvas so their bounding boxes don't
 * overlap. Anchored CCs (any pinned or previously-positioned member) stay
 * in their absolute coords — user placement and inter-solve continuity
 * win. Cold CCs (no pin, no warm start) get translated into rows starting
 * below the union of anchored bboxes (or at origin if none).
 *
 * Largest cold CCs go first within a row so smaller CCs slot into the
 * leftover space. Row width caps at the larger of any single cold CC's
 * width and a fixed default (≈80% of 1080p at 0.8 zoom). Stable id-hash
 * ties keep re-solves deterministic.
 *
 * Called twice in a non-drag solve — once after seeding (so the solver
 * starts with CCs in distinct slots and parent gravity pulls toward each
 * CC's own slot centroid) and once after the solver settles (CCs may have
 * grown / shrunk; re-pack tightens layout before the tidy phase).
 */
function packComponents(
  ccs: readonly Component[],
  pos: Map<string, { x: number; y: number }>,
  size: ReadonlyMap<string, { w: number; h: number }>,
): void {
  // Single-CC fast path: nothing to pack against. Leave positions in the
  // solver's natural frame so single-CC graphs are byte-identical to the
  // pre-CC-decomposition behaviour (modulo seed jitter, which is
  // deterministic). Downstream `buildSpace` re-origins around contents
  // anyway, so absolute coords here don't affect the rendered viewport.
  if (ccs.length < 2) return;

  interface Box {
    readonly cc: Component;
    minX: number;
    minY: number;
    w: number;
    h: number;
  }
  const boxOf = (cc: Component): Box => {
    let mnX = Infinity, mnY = Infinity, mxX = -Infinity, mxY = -Infinity;
    for (const id of cc.ids) {
      const p = pos.get(id);
      const s = size.get(id);
      if (!p || !s) continue;
      mnX = Math.min(mnX, p.x);
      mnY = Math.min(mnY, p.y);
      mxX = Math.max(mxX, p.x + s.w);
      mxY = Math.max(mxY, p.y + s.h);
    }
    if (!Number.isFinite(mnX)) {
      mnX = 0; mnY = 0; mxX = 0; mxY = 0;
    }
    return { cc, minX: mnX, minY: mnY, w: mxX - mnX, h: mxY - mnY };
  };

  const anchored: Box[] = [];
  const cold: Box[] = [];
  for (const cc of ccs) {
    const b = boxOf(cc);
    if (cc.isCold) cold.push(b);
    else anchored.push(b);
  }
  if (cold.length === 0) return;

  // Origin for cold CC rows: below all anchored CCs (if any) so anchored
  // and cold strips read top-to-bottom. No anchored: rows start at (0, 0).
  let originX = 0;
  let originY = 0;
  let anchoredWidth = 0;
  if (anchored.length > 0) {
    let aMinX = Infinity, aMaxX = -Infinity, aMaxY = -Infinity;
    for (const b of anchored) {
      aMinX = Math.min(aMinX, b.minX);
      aMaxX = Math.max(aMaxX, b.minX + b.w);
      aMaxY = Math.max(aMaxY, b.minY + b.h);
    }
    originX = aMinX;
    originY = aMaxY + CC_PACK_GAP;
    anchoredWidth = aMaxX - aMinX;
  }

  // Row width: max of (anchored span, largest cold CC, default cap). A
  // single huge cold CC gets its own row; otherwise multiple fit per row.
  let maxColdW = 0;
  for (const b of cold) maxColdW = Math.max(maxColdW, b.w);
  const rowMaxW = Math.max(anchoredWidth, maxColdW, CC_PACK_ROW_WIDTH);

  // Deterministic shelf order: largest area first (better fit), id-hash tie
  // break. Matches the previous isolated-row stable order.
  cold.sort((a, b) => {
    const aa = a.w * a.h;
    const bb = b.w * b.h;
    if (aa !== bb) return bb - aa;
    const ka = minId(a.cc.ids);
    const kb = minId(b.cc.ids);
    return ka < kb ? -1 : ka > kb ? 1 : 0;
  });

  let cursorX = originX;
  let cursorY = originY;
  let rowH = 0;
  for (const b of cold) {
    if (cursorX > originX && cursorX + b.w > originX + rowMaxW) {
      cursorX = originX;
      cursorY += rowH + CC_PACK_GAP;
      rowH = 0;
    }
    const dx = cursorX - b.minX;
    const dy = cursorY - b.minY;
    if (dx !== 0 || dy !== 0) {
      for (const id of b.cc.ids) {
        const p = pos.get(id);
        if (p) { p.x += dx; p.y += dy; }
      }
    }
    cursorX += b.w + CC_PACK_GAP;
    rowH = Math.max(rowH, b.h);
  }
}

// ---- Seed ------------------------------------------------------------------

function seedPositions(
  nodes: readonly StressNode[],
  edges: readonly StressEdge[],
  previous: ReadonlyMap<string, Rect>,
  pinned: ReadonlyMap<string, Rect>,
): Map<string, { x: number; y: number }> {
  const pos = new Map<string, { x: number; y: number }>();

  // Pass 1: pinned > previous > (to be filled).
  for (const n of nodes) {
    const pin = pinned.get(n.id);
    if (pin) {
      pos.set(n.id, { x: pin.x, y: pin.y });
      continue;
    }
    const prev = previous.get(n.id);
    if (prev) {
      pos.set(n.id, { x: prev.x, y: prev.y });
    }
  }

  // Pass 2: cold-start nodes need an initial arrangement that already
  // reflects the dependency flow, so the solver starts close to the final
  // shape instead of having to rotate a square blob into a wide band. We
  // use a topological-rank seed: nodes with more outgoing-than-incoming
  // edges (sources) get low x, pure sinks get high x, balanced hubs sit
  // in the middle. Stable id hash breaks ties on y, so multi-node layers
  // don't stack on top of each other.
  const unplacedIds = nodes.filter((n) => !pos.has(n.id)).map((n) => n.id);
  const idSet = new Set(unplacedIds);
  const inDeg = new Map<string, number>();
  const outDeg = new Map<string, number>();
  for (const id of unplacedIds) {
    inDeg.set(id, 0);
    outDeg.set(id, 0);
  }
  for (const e of edges) {
    if (idSet.has(e.to)) inDeg.set(e.to, (inDeg.get(e.to) ?? 0) + 1);
    if (idSet.has(e.from)) outDeg.set(e.from, (outDeg.get(e.from) ?? 0) + 1);
  }

  // Topo-rank: longest path from any pure source, computed iteratively.
  // Not using longest-path-DFS because cycles break it; instead we run a
  // bounded relaxation on an imbalance score that behaves sensibly on
  // cycles (they land at mid-rank).
  const rank = new Map<string, number>();
  for (const id of unplacedIds) {
    const inD = inDeg.get(id) ?? 0;
    const outD = outDeg.get(id) ?? 0;
    // Initial rank: pure sources = 0, pure sinks = high, mixed = linearly
    // interpolated. Later relaxation refines this.
    rank.set(id, inD - outD);
  }
  // Relaxation: each edge (i→j) nudges rank[j] toward rank[i]+1, averaged.
  for (let iter = 0; iter < 8; iter++) {
    const nextRank = new Map(rank);
    for (const e of edges) {
      if (!idSet.has(e.from) || !idSet.has(e.to)) continue;
      const rj = rank.get(e.to)!;
      const ri = rank.get(e.from)!;
      nextRank.set(e.to, (rj + (ri + 1)) / 2);
    }
    for (const [k, v] of nextRank) rank.set(k, v);
  }
  // Bucket by rank (rounded) to form pseudo-layers.
  const byRank = new Map<number, string[]>();
  for (const id of unplacedIds) {
    const r = Math.round(rank.get(id) ?? 0);
    const arr = byRank.get(r) ?? [];
    arr.push(id);
    byRank.set(r, arr);
  }
  const ranksSorted = [...byRank.keys()].sort((a, b) => a - b);
  const layerSpacing = NODE_W + OVERLAP_GAP * 2; // generous horizontal gap
  const rowSpacing = NODE_H + OVERLAP_GAP;
  ranksSorted.forEach((r, layerIdx) => {
    const group = byRank.get(r)!;
    // Stable order within layer by id hash so runs are deterministic.
    group.sort((a, b) => (hash01(a) < hash01(b) ? -1 : 1));
    const colX = layerIdx * layerSpacing;
    const stackH = group.length * rowSpacing;
    const startY = -stackH / 2;
    group.forEach((id, i) => {
      const jy = (hash01(id + ":jy") - 0.5) * 20;
      pos.set(id, {
        x: colX + (hash01(id + ":jx") - 0.5) * 20,
        y: startY + i * rowSpacing + jy,
      });
    });
  });

  // Pass 3: cycle members re-seed on a RING. An SCC relaxes to one shared
  // rank, so the buckets above stack it into a near-collinear column — a
  // saddle the gradient can barely leave (pair forces act along the line;
  // the axis-align nudge and the organize column snap re-crystallize it),
  // leaving the cycle's closing edge doubled back across the stack. The
  // ring is the stress shape a cycle wants anyway; the SGD phase only has
  // to refine it. Ring order is a deterministic cycle walk so graph-
  // adjacent members sit adjacent on the ring. SCCs touching a warm or
  // pinned member are left alone — that geometry is real.
  const sizeOf = new Map(nodes.map((n) => [n.id, { w: n.w, h: n.h }]));
  const compOf = sccComponents(unplacedIds, edges);
  const byScc = new Map<number, string[]>();
  for (const id of unplacedIds) {
    const c = compOf.get(id);
    if (c === undefined) continue;
    const arr = byScc.get(c) ?? [];
    arr.push(id);
    byScc.set(c, arr);
  }
  const sccAdj = new Map<string, string[]>();
  for (const e of edges) {
    if (e.from === e.to) continue;
    const c = compOf.get(e.from);
    if (c === undefined || c !== compOf.get(e.to)) continue;
    const arr = sccAdj.get(e.from) ?? [];
    arr.push(e.to);
    sccAdj.set(e.from, arr);
  }
  for (const list of sccAdj.values()) list.sort();
  for (const comp of [...byScc.keys()].sort((a, b) => a - b)) {
    const members = byScc.get(comp)!.sort();
    const n = members.length;
    if (n < 3) continue; // a 2-cycle stacks fine — a ring of two IS a stack
    // Cycle walk: smallest id, then smallest unvisited in-SCC neighbor;
    // jump to the smallest unvisited member when stuck (non-simple SCCs).
    const visited = new Set<string>();
    const order: string[] = [];
    let cur: string | undefined = members[0];
    while (order.length < n) {
      if (cur === undefined || visited.has(cur)) {
        cur = members.find((m) => !visited.has(m));
      }
      if (cur === undefined) break;
      visited.add(cur);
      order.push(cur);
      cur = (sccAdj.get(cur) ?? []).find((m) => !visited.has(m));
    }
    // Ring center = centroid of the bucket seeds; radius sized so
    // neighboring chords clear a node comfortably.
    let cx = 0;
    let cy = 0;
    for (const id of members) {
      const p = pos.get(id)!;
      const s = sizeOf.get(id)!;
      cx += p.x + s.w / 2;
      cy += p.y + s.h / 2;
    }
    cx /= n;
    cy /= n;
    const chord = NODE_W + OVERLAP_GAP;
    const radius = chord / (2 * Math.sin(Math.PI / n));
    order.forEach((id, k) => {
      const theta = -Math.PI / 2 + (2 * Math.PI * k) / n;
      const s = sizeOf.get(id)!;
      pos.set(id, {
        x: cx + radius * Math.cos(theta) - s.w / 2,
        y: cy + radius * Math.sin(theta) - s.h / 2,
      });
    });
  }

  return pos;
}

// ---- One gradient step -----------------------------------------------------

interface StepArgs {
  nodeIds: readonly string[];
  pos: Map<string, { x: number; y: number }>;
  size: Map<string, { w: number; h: number }>;
  pinned: ReadonlyMap<string, Rect>;
  edges: readonly EdgeForce[];
  directedEdges: readonly StressEdge[];
  ports: readonly StressPort[];
  connectedSet: ReadonlySet<string>;
  siblingSet: ReadonlySet<string>;
  lr: number;
  stepCap: number;
  /** When true, apply sand-mode friction in step 7 (static + kinetic). */
  sand: boolean;
  /** Static-friction threshold override (default SAND_F_STATIC). Idle
   *  warm re-solves pass SAND_F_STATIC_HOLD to keep the organized layout
   *  from creeping; drag keeps the tuned default. */
  sandStatic?: number;
  /**
   * Ghost nodes — repulsion (force 4) skips any pair where either endpoint
   * is a ghost. Springs, flow, alignment, gravity, and ports still apply
   * normally. Used by the ghost-repair phase: a stretched-edge node is
   * temporarily made non-repulsive so the spring can pull it through
   * intervening nodes toward its peer; once it arrives, ghost is cleared
   * and normal repulsion pushes it into the nearest free spot.
   */
  ghostSet?: ReadonlySet<string>;
  /**
   * Wall-anchor settle — maps a ported node id to the target CENTRE x of its
   * wall column. A horizontal spring (stiffness `wallK`) pulls the node toward
   * that x. Set only during the final wall-anchor phase; interior nodes are
   * passed via `pinned` so they stay put and act as obstacles.
   */
  wallTarget?: ReadonlyMap<string, number>;
  wallK?: number;
  /**
   * Frame cohesion (forced partition): coarse id → group id. Each mapped
   * node feels a weak spring toward ITS group's live centroid (recomputed
   * from `pos` every call — the edit-calibration collect passes baseline
   * positions and so measures the same rule at the pre-edit state, which
   * is what keeps the mirror exact). Weight = `frameK`.
   */
  frameGroupOf?: ReadonlyMap<string, string>;
  frameK?: number;
  /** Nested `group by`: group → parent group. A member also feels each
   *  ancestor group's live centroid at `frameK ×
   *  FRAME_ANCESTOR_COHESION_DECAY^level` (level 1 = immediate parent). */
  frameParents?: ReadonlyMap<string, string>;
  /** Optional vertical companion to `wallTarget` — pulls the node's centre y
   *  toward a FREE SLOT along the wall column (edit solves: the held co-wall
   *  peers are trim-anchored and cannot make room, so the newcomer is aimed
   *  at the nearest unoccupied band instead). */
  wallTargetY?: ReadonlyMap<string, number>;
  /** Collect-only: accumulate the gradient into this map and return
   *  without moving anything. Used to calibrate trim anchors. */
  collect?: Map<string, { x: number; y: number }>;
  /**
   * Formation anchors — the organize pass's DECISIONS (column x, stack
   * slot y) expressed as standing spring forces instead of placement.
   * They persist through every later phase and every warm solve, so the
   * crystallized formation is part of the force system itself: the final
   * state is a genuine equilibrium, and a drag pulls AGAINST the
   * formation rather than waking a different equilibrium.
   */
  anchors?: ReadonlyMap<string, { x: number; y: number }>;
  anchorK?: number;
}

function stepGradient(args: StepArgs): number {
  const { nodeIds, pos, size, pinned } = args;

  // Accumulate ∇E per node. Pinned nodes get no update.
  const grad = new Map<string, { x: number; y: number }>();
  for (const id of nodeIds) grad.set(id, { x: 0, y: 0 });

  // 1. Connection stress (spring toward edge-specific ideal length).
  for (const e of args.edges) {
    addSpring(pos, size, grad, e.from, e.to, e.idealLen, e.stiffness, e.fromAnchor, e.toAnchor);
  }

  // 2. Flow bias — constant horizontal force per directed edge. Source
  //    gets a leftward push, target gets a rightward push. Value doesn't
  //    depend on current positions, which is what gives cycles their free
  //    geometry (opposing contributions cancel on a closed loop) while DAG
  //    chains accumulate per-edge bias that sources drift left under.
  //    Connection stress balances the pull so chains sit at roughly ideal
  //    edge length, slightly stretched.
  for (const e of args.directedEdges) {
    grad.get(e.from)!.x += W_FLOW; // dE/dx_i > 0 → GD moves i left
    grad.get(e.to)!.x -= W_FLOW; //   dE/dx_j < 0 → GD moves j right
  }

  // 2b. Frame cohesion — a weak spring from each grouped node toward its
  //     group's live centroid, so a forced group congeals instead of
  //     scattering (frame groups; sparse intra-group edges give physics
  //     nothing else to hold the family together with). Nested groups add
  //     decayed pulls toward each ANCESTOR group's centroid (accumulated
  //     over every node whose group CHAIN contains the ancestor).
  if (args.frameGroupOf && args.frameK) {
    const chainOf = (g: string): string[] => {
      // Visited-guard: a malformed parents map with a cycle must not spin
      // a live solve forever (the engine never emits cycles; insurance).
      const chain = [g];
      const seen = new Set(chain);
      let cur = args.frameParents?.get(g);
      while (cur !== undefined && !seen.has(cur)) {
        chain.push(cur);
        seen.add(cur);
        cur = args.frameParents!.get(cur);
      }
      return chain;
    };
    const cx = new Map<string, { x: number; y: number; n: number }>();
    for (const id of args.nodeIds) {
      const g = args.frameGroupOf.get(id);
      if (g === undefined) continue;
      const p = pos.get(id)!;
      const s = size.get(id)!;
      for (const anc of chainOf(g)) {
        const acc = cx.get(anc) ?? { x: 0, y: 0, n: 0 };
        acc.x += p.x + s.w / 2;
        acc.y += p.y + s.h / 2;
        acc.n++;
        cx.set(anc, acc);
      }
    }
    for (const id of args.nodeIds) {
      const g = args.frameGroupOf.get(id);
      if (g === undefined) continue;
      const p = pos.get(id)!;
      const s = size.get(id)!;
      const chain = chainOf(g);
      for (let level = 0; level < chain.length; level++) {
        const acc = cx.get(chain[level]!)!;
        if (acc.n < 2) continue; // a lone member has nothing to congeal with
        const k = args.frameK * Math.pow(FRAME_ANCESTOR_COHESION_DECAY, level);
        grad.get(id)!.x += k * (p.x + s.w / 2 - acc.x / acc.n);
        grad.get(id)!.y += k * (p.y + s.h / 2 - acc.y / acc.n);
      }
    }
  }

  // 3. Axis alignment — for each connected edge, pick the smaller of
  //     |dx| vs |dy| and nudge that component toward zero. Effect is to
  //     rotate edges toward the nearest horizontal or vertical, giving the
  //     layout a grid-ish feel while staying a soft nudge, not a constraint.
  const wAlign = W_ALIGN;
  for (const e of args.edges) {
    const a = pos.get(e.from)!;
    const b = pos.get(e.to)!;
    const sa = size.get(e.from)!;
    const sb = size.get(e.to)!;
    const dx = (b.x + sb.w / 2) - (a.x + sa.w / 2);
    const dy = (b.y + sb.h / 2) - (a.y + sa.h / 2);
    if (Math.abs(dx) > Math.abs(dy)) {
      // Edge is more horizontal → pull dy toward 0.
      const f = wAlign * dy;
      grad.get(e.from)!.y -= f; // A moves toward B's y
      grad.get(e.to)!.y += f; //   B moves toward A's y
    } else {
      // More vertical → pull dx toward 0.
      const f = wAlign * dx;
      grad.get(e.from)!.x -= f;
      grad.get(e.to)!.x += f;
    }
  }

  // 4. Two-tier repulsion:
  //    • Strong short-range — applies to every pair (even connected).
  //      Prevents rectangles from collapsing into each other regardless
  //      of how hard a spring pulls. Zero above a tight cutoff so it
  //      doesn't fight the springs at normal distances.
  //    • Weak medium-range — only between unrelated pairs (skips both
  //      connected and sibling pairs). Gives the overall "cloud" feel
  //      without preventing a hub's children from clustering tight.
  const ghostSet = args.ghostSet;
  // Both repulsion tiers are zero beyond a hard gap cutoff (strong: 40,
  // weak: REPULSE_WEAK_CUTOFF). A pair whose centres are farther apart than
  // (weak cutoff + the two nodes' silhouette reach) contributes EXACTLY zero
  // to either tier, so the per-pair geometry (hypot + four silhouette radii)
  // is wasted work for it. We bound each node's reach by its half-diagonal
  // (an upper bound on ellipseR/roundedRectR in any direction) and skip the
  // pair the instant |dx| or |dy| exceeds the range — both are lower bounds
  // on the centre distance, so the skip is conservative. This is a pure
  // early-out: the iteration order and every applied force are byte-identical
  // to the full O(n²) loop (skipped pairs hit `totalF === 0` anyway); it just
  // drops the dominant trig cost for the far majority of pairs on big spaces.
  let maxHalfDiag = 0;
  for (const id of nodeIds) {
    const s = size.get(id)!;
    const hd = Math.hypot(s.w / 2, s.h / 2);
    if (hd > maxHalfDiag) maxHalfDiag = hd;
  }
  const REPULSE_RANGE = REPULSE_WEAK_CUTOFF + 2 * maxHalfDiag;

  // Per-pair force, shared by both enumeration strategies below. `i < j`
  // by construction so each unordered pair is handled once; the
  // `|dx|/|dy| > RANGE` filter is the provably-zero early-out.
  const applyPair = (i: number, j: number) => {
    const idA = nodeIds[i]!;
    const idB = nodeIds[j]!;
    // Ghost nodes are transparent to repulsion — both tiers. Springs
    // and flow still apply (handled outside this pair loop).
    if (ghostSet && (ghostSet.has(idA) || ghostSet.has(idB))) return;
    const pA = pos.get(idA)!;
    const pB = pos.get(idB)!;
    const sA = size.get(idA)!;
    const sB = size.get(idB)!;
    const dx = (pA.x + sA.w / 2) - (pB.x + sB.w / 2);
    const dy = (pA.y + sA.h / 2) - (pB.y + sB.h / 2);
    if (Math.abs(dx) > REPULSE_RANGE || Math.abs(dy) > REPULSE_RANGE) return;
    const dist = Math.hypot(dx, dy) + 1e-9;
    const ux = dx / dist;
    const uy = dy / dist;
    // Weak (smooth, medium-range) uses the ellipse silhouette. Strong
    // (short-range anti-overlap) uses the actual rounded-rect silhouette so
    // it hugs the visible node shape — nodes can't visually overlap even
    // when approaching at 45°, which ellipse-gap would allow.
    const rAe = ellipseR(ux, uy, sA.w / 2, sA.h / 2);
    const rBe = ellipseR(ux, uy, sB.w / 2, sB.h / 2);
    const rAr = roundedRectR(ux, uy, sA.w / 2, sA.h / 2);
    const rBr = roundedRectR(ux, uy, sB.w / 2, sB.h / 2);
    const gapEllipse = dist - rAe - rBe;
    const gapRect = dist - rAr - rBr;

    let totalF = 0;
    if (gapRect <= REPULSE_STRONG_CUTOFF) {
      const d = Math.max(REPULSE_STRONG_FLOOR, gapRect);
      // 1/d² falloff, concentrated near zero-gap (where it stops overlap)
      // and ~zero at spring operating distances. Subtracting the boundary
      // value makes the zero crossing at the cutoff smooth (no jitter kick).
      totalF +=
        W_REPULSE_STRONG / (d * d) -
        W_REPULSE_STRONG / (REPULSE_STRONG_CUTOFF * REPULSE_STRONG_CUTOFF);
    }
    const key = `${idA}|${idB}`;
    // Physics owns EVERY pair's distance (structure only aligns on top):
    // connected pairs are the springs' jurisdiction (two-sided at the
    // ideal), but SIBLINGS — nothing controls them once fully exempted,
    // and in a dense mesh nearly every pair shares a peer, so the whole
    // core had NO spacing force and collapsed into a carpet (live demo,
    // ord001 saga hub). Siblings now feel SCALED weak repulsion: strong
    // enough to space a mesh, gentle enough for a hub's fan to stay
    // clustered.
    if (!args.connectedSet.has(key) && gapEllipse <= REPULSE_WEAK_CUTOFF) {
      const d = Math.max(REPULSE_WEAK_FLOOR, gapEllipse);
      const w = args.siblingSet.has(key)
        ? W_REPULSE_WEAK * SIBLING_REPULSE_SCALE
        : W_REPULSE_WEAK;
      totalF += w / (d * d);
    }
    if (totalF === 0) return;

    grad.get(idA)!.x -= totalF * ux;
    grad.get(idA)!.y -= totalF * uy;
    grad.get(idB)!.x += totalF * ux;
    grad.get(idB)!.y += totalF * uy;
  };

  // Below the threshold the plain O(n²) sweep (with the per-pair early-out)
  // is fastest — bucketing/sort overhead isn't worth it, and output is
  // byte-identical to the historical loop. Above it, quadratic enumeration
  // alone dominates (500 nodes ≈ 125k iterations PER gradient pass × ~10
  // passes), so switch to a spatial grid (cell = REPULSE_RANGE): any pair
  // within the interaction box lands in the same or an adjacent cell, so
  // each node tests only its 3×3 neighbourhood. Same pairs visited in the
  // same i<j, ascending-j order → same accumulation, same result.
  const GRID_THRESHOLD = 300;
  if (nodeIds.length <= GRID_THRESHOLD) {
    for (let i = 0; i < nodeIds.length; i++) {
      for (let j = i + 1; j < nodeIds.length; j++) applyPair(i, j);
    }
  } else {
    const cellKey = (gx: number, gy: number) => gx * 1e7 + gy;
    const grid = new Map<number, number[]>();
    const cellX = new Int32Array(nodeIds.length);
    const cellY = new Int32Array(nodeIds.length);
    for (let i = 0; i < nodeIds.length; i++) {
      const p = pos.get(nodeIds[i]!)!;
      const s = size.get(nodeIds[i]!)!;
      const gx = Math.floor((p.x + s.w / 2) / REPULSE_RANGE);
      const gy = Math.floor((p.y + s.h / 2) / REPULSE_RANGE);
      cellX[i] = gx;
      cellY[i] = gy;
      const key = cellKey(gx, gy);
      const bucket = grid.get(key);
      if (bucket) bucket.push(i);
      else grid.set(key, [i]);
    }
    const candidates: number[] = [];
    for (let i = 0; i < nodeIds.length; i++) {
      const gx = cellX[i]!;
      const gy = cellY[i]!;
      candidates.length = 0;
      for (let dgx = -1; dgx <= 1; dgx++) {
        for (let dgy = -1; dgy <= 1; dgy++) {
          const bucket = grid.get(cellKey(gx + dgx, gy + dgy));
          if (!bucket) continue;
          for (const j of bucket) if (j > i) candidates.push(j);
        }
      }
      candidates.sort((a, b) => a - b);
      for (const j of candidates) applyPair(i, j);
    }
  }

  // 5. Parent gravity — pull toward centroid. Stabilises disconnected groups.
  let cx = 0,
    cy = 0;
  for (const id of nodeIds) {
    const p = pos.get(id)!;
    cx += p.x;
    cy += p.y;
  }
  cx /= nodeIds.length;
  cy /= nodeIds.length;
  for (const id of nodeIds) {
    const p = pos.get(id)!;
    const g = grad.get(id)!;
    g.x += W_PARENT * (p.x - cx);
    g.y += W_PARENT * (p.y - cy);
  }

  // 6. Port bias — same constant-force formulation as flow. An "out" port
  //    pushes its node right; "in" pushes left. Stronger per-port than
  //    flow per edge because the port encodes a boundary: a module with
  //    an incoming external port is explicitly an entry point for this
  //    space and should sit near the left wall. An "inout" port pulls both
  //    ways at once, i.e. not at all.
  for (const port of args.ports) {
    const g = grad.get(port.nodeId);
    if (!g || port.direction === "inout") continue;
    if (port.direction === "out") g.x -= W_PORT;
    else g.x += W_PORT;
  }

  // 6c. Wall anchor — horizontal spring pulling a ported node's centre x toward
  //     its wall column. Unlike the constant port bias above, this has a finite
  //     rest position (the wall x), so a node with no other force on it settles
  //     AT the wall instead of drifting. Only the final wall-anchor phase sets
  //     `wallTarget`; every other pass leaves it undefined and skips this.
  if (args.wallTarget && args.wallK) {
    for (const [id, targetCx] of args.wallTarget) {
      const g = grad.get(id);
      if (!g) continue;
      const p = pos.get(id)!;
      const s = size.get(id)!;
      const cx = p.x + s.w / 2;
      g.x += args.wallK * (cx - targetCx); // dE/dx > 0 when right of wall → GD pulls left
    }
  }
  if (args.wallTargetY && args.wallK) {
    for (const [id, targetCy] of args.wallTargetY) {
      const g = grad.get(id);
      if (!g) continue;
      const p = pos.get(id)!;
      const s = size.get(id)!;
      g.y += args.wallK * (p.y + s.h / 2 - targetCy);
    }
  }

  // 7. Apply gradient with step cap. In sand mode, gate the gradient
  //    itself: static friction holds nodes whose net force is below
  //    SAND_F_STATIC; kinetic friction subtracts SAND_F_KINETIC along the
  //    motion direction once moving. Friction operates on the gradient
  //    (force units), not on the post-lr displacement — that way the
  //    threshold means the same thing across drag / incremental budgets
  //    (different lr, different stepCap) and tracks force scale rather
  //    than per-iteration motion scale.
  if (args.collect) {
    for (const [id, g] of grad) args.collect.set(id, { x: g.x, y: g.y });
    return 0;
  }

  let totalSqMove = 0;
  for (const id of nodeIds) {
    if (pinned.has(id)) continue;
    const g = grad.get(id)!;
    let gx = g.x;
    let gy = g.y;
    const p = pos.get(id)!;
    const anchorT = args.anchors?.get(id);
    const anchorK = anchorT && args.anchorK ? args.anchorK : 0;
    if (args.sand) {
      // The static gate must judge the NET force including the anchor
      // spring: an anchored node at its equilibrium has base ≈ −K(x−t) —
      // large but BALANCED. Gating on base alone re-mobilizes it every
      // frame, and the whole board creeps ~F·lr·α per frame, forever.
      const netX = gx + anchorK * (p.x - (anchorT?.x ?? 0));
      const netY = gy + anchorK * (p.y - (anchorT?.y ?? 0));
      const gMag = Math.hypot(netX, netY);
      if (gMag < (args.sandStatic ?? SAND_F_STATIC)) continue;
      const remaining = gMag - SAND_F_KINETIC;
      if (remaining <= 0) continue;
      const k = remaining / gMag;
      gx *= k;
      gy *= k;
    }
    // ONE combined update: base force explicit, anchor spring implicit —
    // Δ = (−lr·g + lr·K·(t−x)) / (1 + lr·K), then step-cap Δ.
    // Backward-Euler on the spring keeps any stiffness stable (explicit
    // diverges near lr·K = 1), and folding the anchor in BEFORE the cap
    // preserves the spring balance K(x−t) = −base at equilibrium: at the
    // trim/formation fixed point Δ = 0 exactly (capping zero is zero).
    // Capping the base step and relaxing afterwards instead stalls a
    // strongly-forced node at x−t ≈ cap/(lr·K), no matter the force.
    let dx = -args.lr * gx;
    let dy = -args.lr * gy;
    if (anchorK > 0) {
      const lrK = args.lr * anchorK;
      dx = (dx + lrK * (anchorT!.x - p.x)) / (1 + lrK);
      dy = (dy + lrK * (anchorT!.y - p.y)) / (1 + lrK);
    }
    const mag = Math.hypot(dx, dy);
    if (mag > args.stepCap && mag > 0) {
      const s = args.stepCap / mag;
      dx *= s;
      dy *= s;
    }
    p.x += dx;
    p.y += dy;
    totalSqMove += dx * dx + dy * dy;
  }
  return totalSqMove;
}

/**
 * Radius of an ellipse (semi-axes `a`, `b`) along unit direction (ux, uy).
 * Smooth, C∞ — no kinks at rectangle corners, so the gradient stays
 * well-behaved in every direction. Used for connection springs and weak
 * medium-range repulsion.
 */
export function ellipseR(ux: number, uy: number, a: number, b: number): number {
  return 1 / Math.hypot(ux / a, uy / b);
}

/**
 * Radius of a rounded rectangle (half-width `a`, half-height `b`, corner
 * radius `r`) along unit direction (ux, uy). Hugs the actual node shape
 * exactly at the flat sides and smoothly arcs through the corners via
 * ray-circle intersection. Used by the STRONG repulsion tier so nodes
 * can't visually overlap each other even at diagonal approach angles
 * where the ellipse would sit inside the rect.
 *
 * Mildly non-smooth at the boundary between corner and flat regions,
 * but the strong tier only fires at d_eff ≤ 20 and that boundary
 * transition is gentle (C⁰ continuous).
 */
const NODE_CORNER_R = 14; // matches the `rounded-xl` (~12-14px) on items
function roundedRectR(ux: number, uy: number, a: number, b: number): number {
  const axs = Math.abs(ux);
  const ays = Math.abs(uy);
  const r = Math.min(NODE_CORNER_R, Math.min(a, b) * 0.6);
  // Distance to flat sides (no corner considered).
  const tSide = Math.min(
    axs > 1e-9 ? a / axs : Infinity,
    ays > 1e-9 ? b / ays : Infinity,
  );
  // The flat-side hit is on a corner region if the orthogonal coord at
  // that t exceeds (a − r) or (b − r) respectively.
  const hitX = tSide * axs;
  const hitY = tSide * ays;
  if (hitX <= a - r || hitY <= b - r) return tSide;
  // Corner: ray from origin intersects a quarter-circle at centre
  // (a−r, b−r) in the first-quadrant equivalent. Solve |t·u − c| = r.
  const cx = a - r;
  const cy = b - r;
  const B = axs * cx + ays * cy;           // u · c
  const C = cx * cx + cy * cy - r * r;     // |c|² − r²
  const disc = B * B - C;
  if (disc < 0) return tSide; // ray misses circle (shouldn't happen geometrically)
  return B + Math.sqrt(disc);
}

/**
 * Accumulate gradient of E = 0.5·k·(d_eff − ideal)² into grad, where
 * d_eff = ‖B − A‖ − r_A(u) − r_B(u) is the ellipse edge-to-edge distance.
 * This is the "pixel gap" between the two nodes' silhouettes, measured
 * along the chord direction. `ideal` values are now in gap-units.
 */
function addSpring(
  pos: ReadonlyMap<string, { x: number; y: number }>,
  size: ReadonlyMap<string, { w: number; h: number }>,
  grad: Map<string, { x: number; y: number }>,
  aId: string,
  bId: string,
  ideal: number,
  k: number,
  /** Slot anchors (neighborhood mode): measure from a member slot inside
   *  a super body, with the member's silhouette. Gradient still lands on
   *  the body. */
  aAnchor?: SlotAnchor,
  bAnchor?: SlotAnchor,
) {
  const a = pos.get(aId)!;
  const b = pos.get(bId)!;
  const sa = aAnchor ?? size.get(aId)!;
  const sb = bAnchor ?? size.get(bId)!;
  // Chord between centres. Positions are stored as top-left; for uniform
  // sizes the centre-to-centre vector equals the top-left-to-top-left
  // vector, but we compute explicitly so mixed sizes still work.
  const acx = a.x + (aAnchor ? aAnchor.dx : sa.w / 2);
  const acy = a.y + (aAnchor ? aAnchor.dy : sa.h / 2);
  const bcx = b.x + (bAnchor ? bAnchor.dx : sb.w / 2);
  const bcy = b.y + (bAnchor ? bAnchor.dy : sb.h / 2);
  let dx = bcx - acx;
  let dy = bcy - acy;
  let dist = Math.hypot(dx, dy) + 1e-9;
  // Coincident centers → ux=uy=0 → ellipseR(0,0)=∞ → ∞·0=NaN, which then
  // infects the whole space via centroid gravity. Deterministic unstick
  // along +x (same trick the SGD phase uses); the spring then pushes the
  // pair apart on the next iterations.
  if (dist < 1e-6) {
    dx = 1e-3;
    dy = 0;
    dist = 1e-3;
  }
  const ux = dx / dist;
  const uy = dy / dist;
  const rA = ellipseR(ux, uy, sa.w / 2, sa.h / 2);
  const rB = ellipseR(ux, uy, sb.w / 2, sb.h / 2);
  const dEff = dist - rA - rB;
  const delta = dEff - ideal;
  // Force flows along the chord direction just like the centre-centre
  // spring; only the "zero-force distance" has changed.
  const ga = grad.get(aId)!;
  const gb = grad.get(bId)!;
  ga.x += -k * delta * ux;
  ga.y += -k * delta * uy;
  gb.x += +k * delta * ux;
  gb.y += +k * delta * uy;
}

// ---- Structural repair -----------------------------------------------------

/**
 * For each non-pinned node, compute its "ideal" position based on where its
 * edges say it should be (average of (peer + flow-bias offset), weighted by
 * edge stiffness), then try to move it there — or to the nearest free
 * rectangle if the exact ideal is occupied. This catches cases the
 * long-edge repair misses: a pure sink whose single edge is at the right
 * distance but in a *terrible direction* (e.g. Stripe upper-middle while
 * Payments is mid-right; the edge isn't overstretched, it just points the
 * wrong way). The move only commits if it lowers node-local energy, so
 * repulsion / flow / stress penalties all get respected.
 */
/**
 * Generator variant — same logic as `placeAtIdeal` but yields `true` each
 * time a single node actually moves, so the animation driver can show each
 * placement as a discrete step. Returns `true` iff the whole pass moved
 * anything. Sync drain: `placeAtIdeal(...)`; animation drains with
 * yields.
 */
function* placeAtIdealSteps(
  nodeIds: readonly string[],
  pos: Map<string, { x: number; y: number }>,
  size: ReadonlyMap<string, { w: number; h: number }>,
  edgeForces: readonly { from: string; to: string; stiffness: number; idealLen: number }[],
  pinned: ReadonlyMap<string, Rect>,
  /** Restrict movers to this set (edit solves — repairs stay as local as
   *  the edit). Omitted → every unpinned node is a candidate. */
  scope?: ReadonlySet<string>,
): Generator<{ movedId: string }, boolean, void> {
  if (nodeIds.length < 3 || edgeForces.length === 0) return false;

  // Build per-node neighbour list with direction flag once, so per-pass
  // inner loops stay O(degree) instead of re-scanning the edge list.
  interface NeighbourRef {
    readonly peer: string;
    /** "out" = this node is source (from) → peer sits to the right.
     *  "in"  = this node is target (to)   → peer sits to the left.   */
    readonly dir: "out" | "in";
    readonly stiffness: number;
    readonly idealLen: number;
  }
  const neighbours = new Map<string, NeighbourRef[]>();
  const connectedPeers = new Map<string, Set<string>>();
  const siblingPeers = new Map<string, Set<string>>();
  for (const id of nodeIds) {
    neighbours.set(id, []);
    connectedPeers.set(id, new Set());
    siblingPeers.set(id, new Set());
  }
  for (const e of edgeForces) {
    neighbours.get(e.from)!.push({ peer: e.to, dir: "out", stiffness: e.stiffness, idealLen: e.idealLen });
    neighbours.get(e.to)!.push({ peer: e.from, dir: "in", stiffness: e.stiffness, idealLen: e.idealLen });
    connectedPeers.get(e.from)!.add(e.to);
    connectedPeers.get(e.to)!.add(e.from);
  }
  // Sibling = pair of distinct nodes sharing at least one connected peer.
  for (const [, peers] of connectedPeers) {
    const arr = [...peers];
    for (let i = 0; i < arr.length; i++) {
      for (let j = i + 1; j < arr.length; j++) {
        siblingPeers.get(arr[i]!)!.add(arr[j]!);
        siblingPeers.get(arr[j]!)!.add(arr[i]!);
      }
    }
  }

  // Node-local energy: just the contribution of THIS node's edges plus
  // pairwise repulsion against other nodes. Cheaper than global energy and
  // avoids a move fixing one node at the expense of the rest of the graph
  // by accident (global energy would still be computed per-candidate, so
  // we use the lightweight version here and let the subsequent gradient
  // round clean up any spillover).
  const nodeEnergy = (id: string): number => {
    const p = pos.get(id)!;
    const s = size.get(id)!;
    const pcx = p.x + s.w / 2;
    const pcy = p.y + s.h / 2;
    let e = 0;
    for (const n of neighbours.get(id) ?? []) {
      const q = pos.get(n.peer)!;
      const qs = size.get(n.peer)!;
      let dx = (q.x + qs.w / 2) - pcx;
      let dy = (q.y + qs.h / 2) - pcy;
      let d = Math.hypot(dx, dy) + 1e-9;
      if (d < 1e-6) { dx = 1e-3; dy = 0; d = 1e-3; } // see addSpring unstick
      const ux = dx / d;
      const uy = dy / d;
      const rSelf = ellipseR(ux, uy, s.w / 2, s.h / 2);
      const rPeer = ellipseR(ux, uy, qs.w / 2, qs.h / 2);
      const dEff = d - rSelf - rPeer;
      const delta = dEff - n.idealLen;
      e += 0.5 * n.stiffness * delta * delta;
      // Direction penalty: if n.dir === "out", peer should be right of p
      // (i.e., q.x > p.x). Otherwise, peer should be left.
      const violation = n.dir === "out" ? p.x - q.x : q.x - p.x;
      if (violation > 0) e += 1500 + violation * violation * 0.25;
    }
    // Two-tier repulsion, mirroring stepGradient. Strong uses rounded-rect
    // silhouette, weak uses the smooth ellipse.
    const myConnected = connectedPeers.get(id)!;
    const mySibling = siblingPeers.get(id)!;
    for (const otherId of nodeIds) {
      if (otherId === id) continue;
      const q = pos.get(otherId)!;
      const qs = size.get(otherId)!;
      let dx = pcx - (q.x + qs.w / 2);
      let dy = pcy - (q.y + qs.h / 2);
      let d = Math.hypot(dx, dy) + 1e-9;
      if (d < 1e-6) { dx = 1e-3; dy = 0; d = 1e-3; } // see addSpring unstick
      const ux = dx / d;
      const uy = dy / d;
      const rSelfE = ellipseR(ux, uy, s.w / 2, s.h / 2);
      const rPeerE = ellipseR(ux, uy, qs.w / 2, qs.h / 2);
      const rSelfR = roundedRectR(ux, uy, s.w / 2, s.h / 2);
      const rPeerR = roundedRectR(ux, uy, qs.w / 2, qs.h / 2);
      const gapEllipse = d - rSelfE - rPeerE;
      const gapRect = d - rSelfR - rPeerR;
      if (gapRect <= REPULSE_STRONG_CUTOFF) {
        e += W_REPULSE_STRONG / Math.max(REPULSE_STRONG_FLOOR, gapRect);
      }
      if (!myConnected.has(otherId) && !mySibling.has(otherId) && gapEllipse <= REPULSE_WEAK_CUTOFF) {
        e += W_REPULSE_WEAK / Math.max(REPULSE_WEAK_FLOOR, gapEllipse);
      }
    }
    return e;
  };

  // Is (x, y) free of rectangles? Uses a small inflation (OVERLAP_GAP)
  // so the candidate placement leaves the same margin the solver wants.
  // Probes with the MOVER'S actual rect — a wide card must not "find" a
  // default-sized hole it doesn't fit in.
  const excludeId = { current: "" };
  const isFree = (cx: number, cy: number): boolean => {
    const ms = size.get(excludeId.current) ?? { w: NODE_W, h: NODE_H };
    const w0 = ms.w;
    const h0 = ms.h;
    // Treat (cx, cy) as node CENTRE, consistent with the ideal formula.
    const x = cx - w0 / 2;
    const y = cy - h0 / 2;
    for (const otherId of nodeIds) {
      if (otherId === excludeId.current) continue;
      const q = pos.get(otherId)!;
      const s = size.get(otherId)!;
      if (
        x < q.x + s.w + OVERLAP_GAP &&
        x + w0 + OVERLAP_GAP > q.x &&
        y < q.y + s.h + OVERLAP_GAP &&
        y + h0 + OVERLAP_GAP > q.y
      ) {
        return false;
      }
    }
    return true;
  };

  // Search outward from `ideal` for the nearest free centre. Spiral at
  // increasing radius; first hit wins.
  const findNearestFree = (
    ideal: { x: number; y: number },
  ): { x: number; y: number } | null => {
    if (isFree(ideal.x, ideal.y)) return ideal;
    const radii = [100, 180, 260, 360, 480, 620];
    const angles = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map(
      (a) => (a * Math.PI) / 180,
    );
    for (const r of radii) {
      for (const a of angles) {
        const x = ideal.x + Math.cos(a) * r;
        const y = ideal.y + Math.sin(a) * r;
        if (isFree(x, y)) return { x, y };
      }
    }
    return null;
  };

  // Iterate: repeat passes until nothing moves. Usually converges in 2-3
  // passes; rarely more than 5 needed.
  let anyMoved = false;
  for (let pass = 0; pass < 6; pass++) {
    let passChanged = false;
    for (const id of nodeIds) {
      if (pinned.has(id)) continue;
      if (scope && !scope.has(id)) continue;
      const refs = neighbours.get(id);
      if (!refs || refs.length === 0) continue;

      // Ideal centre = weighted avg of (peer_centre + flow_offset).
      let sx = 0;
      let sy = 0;
      let sw = 0;
      const mySize = size.get(id)!;
      let avgIdeal = 0;
      for (const n of refs) {
        const q = pos.get(n.peer)!;
        const qS = size.get(n.peer)!;
        const qCx = q.x + qS.w / 2;
        const qCy = q.y + qS.h / 2;
        const flowDx = n.dir === "out" ? -n.idealLen : n.idealLen;
        sx += n.stiffness * (qCx + flowDx);
        sy += n.stiffness * qCy;
        sw += n.stiffness;
        avgIdeal += n.idealLen;
      }
      avgIdeal /= refs.length;
      const idealCx = sx / sw;
      const idealCy = sy / sw;
      const curP = pos.get(id)!;
      const curCx = curP.x + mySize.w / 2;
      const curCy = curP.y + mySize.h / 2;

      const distFromIdeal = Math.hypot(idealCx - curCx, idealCy - curCy);
      if (distFromIdeal < avgIdeal * 0.25) continue; // already close

      excludeId.current = id;
      const spot = findNearestFree({ x: idealCx, y: idealCy });
      if (!spot) continue;

      const savedP = curP;
      const beforeE = nodeEnergy(id);
      pos.set(id, { x: spot.x - mySize.w / 2, y: spot.y - mySize.h / 2 });
      const afterE = nodeEnergy(id);
      if (afterE < beforeE - 500) {
        passChanged = true;
        anyMoved = true;
        yield { movedId: id };
      } else {
        pos.set(id, savedP);
      }
    }
    if (!passChanged) break;
  }
  return anyMoved;
}

/**
 * Ghost-mode repair of the worst-stretched edges.
 *
 * The gradient descent settles into a local minimum that frequently has
 * obviously-wrong long edges — typically because intervening nodes block
 * the spring from pulling its endpoint home. Strong short-range repulsion
 * (force 4, both tiers) is what does the blocking.
 *
 * Repair strategy: for each overstretched edge, pick the less-constrained
 * endpoint (mover) and mark it as a "ghost". Ghost nodes are transparent
 * to repulsion in `stepGradient` — every other force still applies. The
 * spring then pulls the mover through any blocking layer toward its peer.
 * After a fixed travel phase we clear ghost and run a few settle iters so
 * strong repulsion bounces the mover out of any overlap into a free spot.
 *
 * Yields per gradient step so animation can show the journey continuously
 * (no teleport-cuts). Sync drain produces identical output via the same
 * generator — anim/sync equality is preserved.
 *
 * Bounds: up to GHOST_MAX_REPAIRS movements per call. Exits early when
 * no edge exceeds the stretch threshold, or when one full repair pass
 * fails to reduce max stretch by GHOST_PROGRESS_EPS (stuck — give up
 * rather than thrash).
 */
const GHOST_MAX_REPAIRS = 15;
const GHOST_ITERS = 25;
const GHOST_SETTLE_ITERS = 5;
const GHOST_LR = 0.06;
const GHOST_STEP_CAP = 20;
const GHOST_SETTLE_LR = 0.04;
const GHOST_SETTLE_STEP_CAP = 8;
const GHOST_PROGRESS_EPS = 0.02; // require ≥2 % drop in max stretch per pass

interface GhostRepairArgs {
  nodeIds: readonly string[];
  pos: Map<string, { x: number; y: number }>;
  size: Map<string, { w: number; h: number }>;
  edges: readonly EdgeForce[];
  directedEdges: readonly StressEdge[];
  ports: readonly StressPort[];
  connectedSet: ReadonlySet<string>;
  siblingSet: ReadonlySet<string>;
  pinned: ReadonlyMap<string, Rect>;
  deg: ReadonlyMap<string, number>;
  /** Restrict repairs to edges with a movable endpoint in this set (edit
   *  solves). Omitted → all overstretched edges are candidates. */
  scope?: ReadonlySet<string>;
}

interface GhostYield {
  readonly phase: "travel" | "settle";
  readonly moverId: string;
}

function* ghostRepairSteps(
  args: GhostRepairArgs,
): Generator<GhostYield, boolean, void> {
  const { nodeIds, pos, size, edges: edgeForces, pinned, deg, scope } = args;
  if (nodeIds.length < 3 || edgeForces.length === 0) return false;

  /** Stretch = silhouette gap minus ideal length, in pixels. */
  const stretchOf = (
    e: { from: string; to: string; idealLen: number },
  ): number => {
    const a = pos.get(e.from)!;
    const b = pos.get(e.to)!;
    const sa = size.get(e.from)!;
    const sb = size.get(e.to)!;
    const dx = (b.x + sb.w / 2) - (a.x + sa.w / 2);
    const dy = (b.y + sb.h / 2) - (a.y + sa.h / 2);
    const d = Math.hypot(dx, dy) + 1e-9;
    const ux = dx / d;
    const uy = dy / d;
    const rA = ellipseR(ux, uy, sa.w / 2, sa.h / 2);
    const rB = ellipseR(ux, uy, sb.w / 2, sb.h / 2);
    return d - rA - rB - e.idealLen;
  };

  /** Worst-stretched edge above threshold, or null if everything fits. */
  const worstEdge = () => {
    let best: { edge: typeof edgeForces[number]; stretch: number } | null = null;
    for (const e of edgeForces) {
      const s = stretchOf(e);
      if (s <= Math.max(120, e.idealLen * 0.35)) continue;
      // Edit solves only repair edges the edit touched.
      if (scope && !scope.has(e.from) && !scope.has(e.to)) continue;
      if (!best || s > best.stretch) best = { edge: e, stretch: s };
    }
    return best;
  };

  let anyMoved = false;
  let lastMaxStretch = Infinity;

  for (let pass = 0; pass < GHOST_MAX_REPAIRS; pass++) {
    const worst = worstEdge();
    if (!worst) return anyMoved;

    // Progress check: if the previous repair didn't meaningfully reduce
    // the worst stretch, give up rather than thrash on a stuck edge.
    if (pass > 0 && worst.stretch > lastMaxStretch * (1 - GHOST_PROGRESS_EPS)) {
      return anyMoved;
    }
    lastMaxStretch = worst.stretch;

    // Pick mover: prefer the eligible endpoint with lower degree (more
    // "freely floating", less anchored by other springs). Ties → smaller
    // id for determinism. Eligible = unpinned, and inside `scope` when an
    // edit solve restricts repairs. If neither endpoint is eligible, this
    // edge is unfixable; exit — other long edges re-rank next round.
    const fromOk =
      !pinned.has(worst.edge.from) && (!scope || scope.has(worst.edge.from));
    const toOk =
      !pinned.has(worst.edge.to) && (!scope || scope.has(worst.edge.to));
    if (!fromOk && !toOk) return anyMoved;
    let moverId: string;
    if (!fromOk) moverId = worst.edge.to;
    else if (!toOk) moverId = worst.edge.from;
    else {
      const dFrom = deg.get(worst.edge.from) ?? 0;
      const dTo = deg.get(worst.edge.to) ?? 0;
      if (dFrom < dTo) moverId = worst.edge.from;
      else if (dTo < dFrom) moverId = worst.edge.to;
      else moverId = worst.edge.from < worst.edge.to ? worst.edge.from : worst.edge.to;
    }

    const ghostSet: ReadonlySet<string> = new Set([moverId]);

    // Travel phase — mover ignores repulsion, springs pull it home.
    for (let i = 0; i < GHOST_ITERS; i++) {
      stepGradient({
        nodeIds,
        pos,
        size,
        pinned,
        edges: edgeForces,
        directedEdges: args.directedEdges,
        ports: args.ports,
        connectedSet: args.connectedSet,
        siblingSet: args.siblingSet,
        lr: GHOST_LR,
        stepCap: GHOST_STEP_CAP,
        sand: false,
        ghostSet,
      });
      yield { phase: "travel", moverId };
    }

    // Settle phase — repulsion re-enabled. If the mover landed in an
    // overlap, strong short-range tier nudges it into the nearest free
    // spot in a few iters.
    for (let i = 0; i < GHOST_SETTLE_ITERS; i++) {
      stepGradient({
        nodeIds,
        pos,
        size,
        pinned,
        edges: edgeForces,
        directedEdges: args.directedEdges,
        ports: args.ports,
        connectedSet: args.connectedSet,
        siblingSet: args.siblingSet,
        lr: GHOST_SETTLE_LR,
        stepCap: GHOST_SETTLE_STEP_CAP,
        sand: false,
      });
      yield { phase: "settle", moverId };
    }

    anyMoved = true;
  }
  return anyMoved;
}

// ---- Degenerate cases ------------------------------------------------------

function emptyResult(): StressResult {
  return {
    positions: new Map(),
    width: 2 * PADDING,
    height: 2 * PADDING,
  };
}

function singleResult(
  n: StressNode,
  pinned: ReadonlyMap<string, Rect>,
  previous: ReadonlyMap<string, Rect>,
): StressResult {
  const pin = pinned.get(n.id);
  const prev = previous.get(n.id);
  const w = pin?.w ?? (n.w > 0 ? n.w : NODE_W);
  const h = pin?.h ?? (n.h > 0 ? n.h : NODE_H);
  // Placement priority mirrors seedPositions: pinned > previous > default.
  // A hand-dragged only-child must stick; a warm-started one must not snap
  // back to the frame corner on every rebuild.
  const anchor = pin ?? prev;
  const x = anchor?.x ?? PADDING;
  const y = anchor?.y ?? PADDING;
  const positions = new Map<string, Rect>();
  positions.set(n.id, { x, y, w, h });
  return {
    positions,
    width: w + 2 * PADDING,
    height: h + 2 * PADDING,
  };
}

// ---- Utilities -------------------------------------------------------------


function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Stable, deterministic 0..1 hash — used for tie-breaking seed positions. */
function hash01(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h = Math.imul(h ^ s.charCodeAt(i), 16777619) >>> 0;
  }
  return (h % 100000) / 100000;
}
